import MuleBadge from '../../../components/MuleBadge';
/**
 * LegsGrid — responsive Legs display for the Pro 360 Order Detail redesign.
 *
 * Wide (>= 1280px): a dense 12-column grid with each leg occupying three
 *   sub-rows (origin, destination, address+memo). Headers cross-highlight
 *   with cells via a hover context.
 * Narrow (< 1280px): one card per leg with origin/destination side-by-side.
 * Stacked (< 560px): cards collapse origin/destination into a single column.
 *
 * Editing: cells are wired to the `useOrderEditing` hook through the
 * `editing` prop. Each `CI`/`CS` cell takes a `legrecno` + `field` pair so
 * blur events route to `editing.updateLegField(...)`. Add-leg placeholders
 * call `editing.addLeg(type, position)` and a per-row `×` button calls
 * `editing.removeLeg(legrecno)`. All access is defensive — when `editing`
 * is undefined the grid degrades to read-only behavior.
 */
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MapPinIcon, PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { OrderLeg, DriverValidationLegContextItem } from '../../../services/orders.service';
import { Icon } from './orderform';
import { Tooltip } from '../../../components/ui/Tooltip';
import { RouteEta } from '../../../components/driver-status-monitor/RouteEta';
import { DriverSelectorCell } from '../../../components/DriverSelectorCell';
import { DriverIconLinks } from '../../../components/DriverIconLinks';
import { legIsInTodaysMoves } from '../../../utils/driverMovesWindow';
import TravelHistoryModal from '../../../components/TravelHistoryModal';
import DispatchInfoModal from '../../../components/leg/DispatchInfoModal';
import LocationInfoModal from '../../../components/leg/LocationInfoModal';
import ClearDriverConfirmModal from '../../../components/leg/ClearDriverConfirmModal';
import SendDispatchConfirmModal from '../../../components/leg/SendDispatchConfirmModal';
import Toggle from '../../../components/Toggle';
import dispatchPaperworkService from '../../../services/dispatchPaperwork.service';
import printingService, { type PrintOptions } from '../../../services/printing.service';
import PrintDialog from '../../../components/printing/PrintDialog';
import { saveAs } from '../../../utils/saveFile';
import { streetTurnLegBlock } from '../../../utils/streetTurnLegBlock';
import LegLocationBulkCheckbox from '../changeAll/LegLocationBulkCheckbox';
import type { LegLocationBulkKey } from '../changeAll/legLocationChangeAll';
import {
  describeLegConflictFields,
  getDispatchScope,
  legConflictFields,
} from '../../../utils/orderLegDiff';
import { computeScopeApptFills } from '../../../utils/scopeDateAutofill';
import { outsideCarrierDateStamp } from '../../../utils/driverArrivalAutofill';
import {
  formatBackendDate,
  parseMdYDate,
  formatMdYDate,
  parseHmTime,
  formatHmTime,
  parseMdYHm,
  formatMdYHm,
} from '../../../utils/dateUtils';
import DateTimePicker, { type DateTimeValue } from '../../../components/DateTimePicker';
import LocationTypeAhead, {
  locationCityState,
  locationDisplayName,
} from '../../../components/LocationTypeAhead';
import type { LocationRecord, LocationSaveResult } from '../../../components/LocationEditDrawer';
import type { DupeLocation } from '../../../services/locations.service';
import api from '../../../services/api';

import { isDeletedLocation } from '../../../types/locations';
import type { LocationLite, LocationInfo } from '../../../types/locations';

// "Add New Location" from a leg's origin/destination typeahead opens the full
// Locations maintenance drawer (create mode). Lazy so its Google Maps /
// contacts / geofence dependencies stay out of the Pro drawer's base bundle
// until someone actually adds a location — same reasoning as LocationMapField.
const LocationEditDrawer = lazy(() =>
  import('../../../components/LocationEditDrawer').then((m) => ({ default: m.LocationEditDrawer })),
);
import {
  needsDropReleaseWarning,
  resolveDeliveryLegnum,
  DROP_RELEASE_WARNING_MESSAGE,
} from '../../../utils/dropReleaseCheck';
import { needsNotReceivingWarning, NOT_RECEIVING_WARNING_MESSAGE } from '../../../utils/notReceivingCheck';
import { needsNotRailbilledWarning, NOT_RAILBILLED_WARNING_MESSAGE } from '../../../utils/notRailbilledCheck';
import { needsMissingPreadviseWarning, MISSING_PREADVISE_WARNING_MESSAGE } from '../../../utils/missingPreadviseCheck';
import { openTroubleTicketWarning, blockingTroubleTicketMessage, legAtOrBeforeRejectedGroup } from '../../../utils/openTroubleTicketCheck';
import { redeliveryExpirationWarning } from '../../../utils/redeliveryExpirationCheck';
import { openRepairOrderWarning } from '../../../utils/openRepairOrderCheck';
import PermissionsContainer from '../../../components/PermissionsContainer';
import RescheduleEditModal from './RescheduleEditModal';
import { WarningConfirmModal } from '../../../components/ui/WarningConfirmModal';
import type { OverrideApprovalRecord } from '../../../services/dispatchOverrideApproval.service';
import { useOrderFormContext } from '../../../contexts/OrderFormContext';
import { usePermissionsMode } from '../../../contexts/PermissionsModeContext';
import {
  canEditDriver,
  canEditOrigin,
  canEditDestination,
  canEditLegtype,
  canEditLegAction2,
  canEditLegDrop,
  canEditOrigAppt,
  canEditDestAppt,
  canClearAppt,
  canEditArrivalDepart,
  planDownstreamApptClears,
  type DownstreamApptClearPlan,
  type LegEdits,
} from '../../../utils/legFieldGuards';
import { formatETA, type LegEta } from '../../../components/driver-status-monitor/etaProjection';
import type { ProjectedStatus } from '../../../components/driver-status-monitor/types';
import type { DriverPayLeg } from '../../../services/proDocuments.service';
import { isBelowMarginLimit } from '../../../utils/marginLimit';
import { templateFieldEditable } from '../templateFieldPolicy';
import { useAdminOverride } from '../AdminOverrideContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Permissive leg shape used by LegsGrid. The redesign reuses the existing
 * `OrderLeg` shape from `services/orders.service` so the API layer doesn't
 * need a parallel type. Most fields are optional because the prototype's
 * mock data and the production payload have slightly different coverage.
 */
export type ProLeg = OrderLeg;

/**
 * Subset of the `useOrderEditing` return value that LegsGrid consumes.
 * Every field is optional so the grid works even if the parallel hook
 * agent hasn't shipped yet — call sites should still wire the prop.
 */
export interface OrderEditingProp {
  effectiveLegs?: OrderLeg[];
  pendingNewLegs?: OrderLeg[];
  pendingRemovedLegrecnos?: number[];
  editedLegs?: Record<number, Partial<OrderLeg>>;
  editedOrderFields?: Record<string, unknown>;
  /**
   * Stale-data conflicts from the most recent save attempt, keyed
   * `leg:{legrecno}:{field}` (plus `__new__` / `__removed__` for whole-leg
   * changes) — see useOrderEditing. Drives the per-leg "changed elsewhere"
   * badge; the save itself was already aborted and the grid reloaded with the
   * fresh DB values by the time these render.
   */
  conflicts?: Record<string, { theirs: unknown; mine: unknown }>;
  /**
   * Raw server-side leg snapshot used when a cell needs the loaded value
   * before edits — e.g. LegSendToMoveToggle's "don't un-send an
   * already-dispatched move" guard, which must compare against what the
   * server has on file, not the user's in-flight overlay.
   */
  originalLegs?: OrderLeg[];
  updateLegField?: (legrecno: number, field: string, value: unknown) => void;
  /** Order-level field writer — used by the delivery-location → consignee copy
   *  prompt to write the order/template consignee. */
  updateOrderField?: (name: string, value: unknown) => void;
  addLeg?: (type: 'first' | 'mid' | 'last', position: number) => void;
  removeLeg?: (
    legrecno: number,
    removeType?: 'origin' | 'destination',
  ) => { ok: boolean; reason?: string };
  restoreLeg?: (legrecno: number) => void;
  clearDriver?: (legrecno: number, options?: { clearDates?: boolean }) => void;
  /** Blanks origApptDate + destApptDate on the given legs with no cascade into
   *  their neighbours — used by the delivery-appt clear to take the downstream
   *  legs with it without writing into a leg the plan deliberately skipped. */
  clearLegApptDates?: (legrecnos: number[]) => void;
  /**
   * Injects save-blocking errors that aren't computed synchronously by
   * `validateChanges` — used here for driver re-qualification results
   * aggregated from DriverSelectorCell.onValidationChange.
   */
  setExtraSaveErrors?: (source: string, errors: string[]) => void;
  /**
   * Map of leg fields the hook flagged stale (server-side change while the
   * user had pending edits). Drives the amber-ring highlight on cells.
   */
  staleFields?: Record<number, string[]>;
  /** Clears a stale-field highlight when the user interacts with the cell. */
  clearStaleField?: (legrecno: number, field: string) => void;
  /** Refetches the pro's order details. Used after an immediate, out-of-band
   *  mutation (e.g. the dispatch-info "Remove Pay and Open Leg" / Clear Leg
   *  action) so the grid picks up the change without a full order save. */
  refresh?: () => Promise<void> | void;
}

export interface LegsGridProps {
  legs: ProLeg[];
  /**
   * Test-only escape hatch for forcing a layout regardless of the wrapper
   * width. ResizeObserver mocking in jsdom is finicky, so tests can pin a
   * layout directly. Production callers should leave this undefined.
   */
  forceLayout?: 'wide' | 'cards' | 'stacked';
  /**
   * Editing hook subset — wires cells through to `updateLegField`, the
   * add/remove APIs, and pending-state markers. Optional for back-compat.
   */
  editing?: OrderEditingProp;
  /** Required to render the real DriverSelectorCell. */
  pronumber?: number;
  /** Order-level context used by driver validation / drawer. */
  customerName?: string;
  hazmat?: string;
  isDaycab?: string;
  isTank?: string;
  overweightPermitRequired?: string;
  /** Order-level fields used to compute DriverSelectorCell.dispatchWarnings —
   * drop-release / not-receiving / not-railbilled prompts that fire on driver
   * assignment. `warnings` is the backend-supplied list (orders.py) — only the
   * not_railbilled code is consumed here. */
  moveType?: string;
  /** Reuse pro # (street turn). With moveType IMPORT, a non-zero value disables
   *  every add-leg control — see utils/streetTurnLegBlock. */
  reuseContainerPro?: number;
  isDrop?: string;
  dropReleaseDatetime?: string;
  deliveryLeg?: number;
  /** Order's pickup/terminate leg pointers — which leg the two leg-location
   *  Change All checkboxes attach to. */
  originateLeg?: number;
  terminateLeg?: number;
  /** Order's billto — used to gate THD-only behavior in the delivery
   *  appt-date reschedule modal that opens when the user edits the
   *  delivery leg's destApptDate. */
  billTo?: number | null;
  /** Current consignee name — shown in the delivery-location → consignee copy
   *  prompt. */
  consigneeName?: string;
  firstRcv?: string;
  warnings?: { code: string; message: string }[];
  /** Count of NEW/PENDING trouble tickets on the order, from the order-details
   *  payload. Order-level, so it needs no per-leg scoping. */
  openTtCount?: number;
  openTickets?: { category: string; status: string }[];
  offhire?: string;
  redeliveryExpirationDate?: string;
  /** Count of open repair orders touching this order's pro or its equipment. */
  openRoCount?: number;
  /**
   * Outside-carrier pseudo-driver code (carrier SCAC or first-6-alphanumeric of
   * the carrier name). When non-empty, every leg's DriverSelectorCell offers it
   * as a selectable option and treats it as a valid driver (qualification
   * bypassed — it's not a real unit). Empty/undefined = no extra option.
   */
  outsideCarrierDriver?: string;
  /**
   * Parent-owned ref that LegsGrid mirrors the latest per-leg driver-validation
   * snapshot into. ProDetailContainer's save handler reads this synchronously
   * at click-time to decide whether to open the override-approval modal —
   * unqualified drivers on incomplete legs no longer block save, they prompt
   * for a justification instead.
   */
  driverValidationSnapshotRef?: React.RefObject<DriverValidationSnapshot>;
  /**
   * Recorded driver-qualification overrides for this PRO. Each leg row's
   * driver cell consults this list (keyed by `${legrecno}:${driver}`) to
   * render an inline indicator when the currently-assigned driver matches
   * an approval — and to suppress re-prompting on the next save.
   */
  overrideApprovals?: OverrideApprovalRecord[];
  /**
   * Per-leg driver-ETA projection keyed by legnum. Drives the ETA column —
   * a clock time colored by projected status. Optional; the column renders
   * blank until the drawer wires this in.
   */
  etaByLeg?: Map<number, LegEta>;
  /**
   * Per-leg driver pay + margin (V1 showmargin read-model) keyed by legnum.
   * Drives the Pay/Margin cells in the details stack. Blank until wired.
   */
  marginByLeg?: Map<number, DriverPayLeg>;
  /** Pro's margin limit (%), used to color a leg's margin red when under it. */
  marginLimit?: number;
  /** True while the per-Pro margin is still loading (fetched after the drawer
   *  opens). Drives a "Loading…" placeholder in the per-leg Pay cell. */
  marginLoading?: boolean;
  /**
   * Template mode ("T"-prefixed pronumber). When true, per-leg editability is
   * driven by the template field policy (approval-aware) instead of the order
   * dispatch guards — see `resolveLegGates`. Orders leave this false/undefined
   * and keep their existing gate behavior byte-for-byte.
   */
  isTemplate?: boolean;
  /**
   * Whether the template is approved (order-level `approvedby` is set). Only
   * meaningful when `isTemplate` — locks every State-2 leg field. Ignored for
   * orders.
   */
  isApproved?: boolean;
  /**
   * CSR/LC template-edit gate. When false on a template, every leg field stays
   * locked regardless of the field policy (the leg still takes the template
   * branch, so it never falls back to order dispatch guards). Defaults to
   * editable; ignored for orders.
   */
  canEditTemplate?: boolean;
}

/** Map of legnum → latest driver-validation result published by a DriverSelectorCell. */
export type DriverValidationSnapshot = Record<
  number,
  { isValid: boolean; missing: string[] }
>;

// ---------------------------------------------------------------------------
// Order-level context — surfaces customer/equipment metadata to the driver
// cell without prop-drilling through every leg row component.
// ---------------------------------------------------------------------------

interface OrderContextCtxValue {
  pronumber?: number;
  /** Template mode — forces the live/ops leg controls (driver, send-to-move)
   *  locked regardless of dispatcher/permission. */
  isTemplate?: boolean;
  customerName?: string;
  hazmat?: string;
  isDaycab?: string;
  isTank?: string;
  overweightPermitRequired?: string;
  /** Effective leg list (post-edits) used for dispatch-scope and validation. */
  allLegs: ProLeg[];
  /** Order-level fields consumed by DriverSelectorCell.dispatchWarnings. */
  moveType?: string;
  /** Why adding a leg is refused on this order (street-turned Import), or
   *  null/undefined when it is allowed. Read by every add-leg control. */
  legAddBlock?: string | null;
  isDrop?: string;
  dropReleaseDatetime?: string;
  deliveryLeg?: number;
  /** The order's own pickup/terminate leg pointers. Read by the two leg-location
   *  Change All checkboxes, which must be drawn on the leg that will ACTUALLY be
   *  written — the one these name — and not on a leg that merely looks like a
   *  pickup. */
  originateLeg?: number;
  terminateLeg?: number;
  /** Order's billto — needed by the delivery-leg destApptDate cell to gate
   *  THD-only behavior in the reschedule modal it opens. */
  billTo?: number | null;
  /** Fired by the delivery leg's destination cell when its location changes,
   *  so LegsGrid can offer to copy it into the order/template consignee. */
  onDeliveryDestChange?: (recno: number, name: string) => void;
  firstRcv?: string;
  warnings?: { code: string; message: string }[];
  /** Count of NEW/PENDING trouble tickets on the order. */
  openTtCount?: number;
  openTickets?: { category: string; status: string }[];
  offhire?: string;
  redeliveryExpirationDate?: string;
  /** Count of open repair orders on this order's pro or equipment. */
  openRoCount?: number;
  /** Outside-carrier pseudo-driver code offered on every leg's driver cell. */
  outsideCarrierDriver?: string;
  /**
   * Stable string derived from edited order fields that affect qualification
   * (overweight flags, chassis/equip/size, etc.). Flowed through every
   * DriverSelectorCell as `revalidationKey` so cells re-run validation when
   * an upstream edit changes the requirements set — without duplicating
   * the validation logic in OrderFormView.
   */
  driverRevalidationKey?: string;
  /**
   * Per-leg validation result aggregator. DriverSelectorCells call this on
   * every onValidationChange so the parent can:
   *   - block save when any non-started leg has an unqualified driver
   *     (via the hook's setExtraSaveErrors)
   */
  recordLegValidation?: (
    legnum: number,
    isValid: boolean | null,
    missingRequirements?: string[],
  ) => void;
  /**
   * Approval lookup keyed by `${legrecno}:${driver}`. LegDriverCell uses this
   * to render the override-approved indicator when the currently-assigned
   * driver matches a recorded approval for the leg.
   */
  overrideApprovalByKey?: Map<string, OverrideApprovalRecord>;
  /** Per-leg driver-ETA projection keyed by legnum, surfaced to LegRow/LegEnd
   *  so the ETA cell reads it without prop-drilling through every leg. */
  etaByLeg?: Map<number, LegEta>;
  /** Per-leg driver pay + margin keyed by legnum, surfaced to LegRow/LegEnd
   *  so the Pay/Margin cells read it without prop-drilling. */
  marginByLeg?: Map<number, DriverPayLeg>;
  /** Pro's margin limit (%) — colors a leg's margin red when under it. */
  marginLimit?: number;
  /** True while per-Pro margin is still loading — drives the Pay "Loading…". */
  marginLoading?: boolean;
}

const OrderContextCtx = React.createContext<OrderContextCtxValue>({
  allLegs: [],
});

// Location detail (hours / appt / contacts / instructions) keyed by recno, for
// the per-location info icon. Populated by LegsGrid from /admin/locations/details.
interface LocationInfoCtxValue {
  getLocationInfo: (recno: number | string | null | undefined) => LocationInfo | undefined;
  openLocationInfo: (recno: number) => void;
}
const LocationInfoCtx = React.createContext<LocationInfoCtxValue>({
  getLocationInfo: () => undefined,
  openLocationInfo: () => {},
});

/**
 * The two leg-location "Apply to all" flags, wired to the ORDER'S OWN pointers.
 *
 * A thin wrapper so the cells do not each have to pull the pointers out of
 * context: which leg is the pickup and which is the terminate is the order's
 * answer (`originateleg`/`terminateleg`), not something to infer from a leg's
 * legtype. The checkbox underneath draws nothing unless THIS leg is the one the
 * pointer names.
 */
const LegLocationBulkFlag: React.FC<{
  fieldKey: LegLocationBulkKey;
  leg: ProLeg;
  editable: boolean;
  children: React.ReactNode;
}> = ({ fieldKey, leg, editable, children }) => {
  const { originateLeg, terminateLeg } = React.useContext(OrderContextCtx);
  return (
    <LegLocationBulkCheckbox
      fieldKey={fieldKey}
      leg={leg}
      pointers={{ originateLeg, terminateLeg }}
      editable={editable}
    >
      {children}
    </LegLocationBulkCheckbox>
  );
};

/** Per-location info icon, shown right of the location name (left of the remove
 *  icon). Orange when the location carries driver special instructions. Renders
 *  nothing until the location's detail has loaded. */
const LocationInfoButton: React.FC<{ recno: number | string | null | undefined }> = ({ recno }) => {
  const { getLocationInfo, openLocationInfo } = React.useContext(LocationInfoCtx);
  const code = recno ? Number(recno) : 0;
  const info = code ? getLocationInfo(code) : undefined;
  if (!code || !info) return null;
  const hasInstructions = !!info.specialinstructions?.trim();
  return (
    <button
      type="button"
      onClick={() => openLocationInfo(code)}
      className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-slate-100 dark:hover:bg-gray-700 shrink-0"
      aria-label={`Location info: ${info.lname}`}
      title={`Location info: ${info.lname}`}
      data-testid={`location-info-${code}`}
    >
      <Icon.info
        className={`w-3.5 h-3.5 ${hasInstructions ? 'text-orange-500' : 'text-slate-600 dark:text-gray-300'}`}
      />
    </button>
  );
};

/** True when `leg` is the last entry in its dispatch scope. */
function isLastInScope(leg: ProLeg, all: ProLeg[]): boolean {
  if (!all.length) return false;
  const scope = getDispatchScope(leg.legrecno, all);
  return scope.length > 0 && scope[scope.length - 1] === leg.legrecno;
}

/**
 * Scope membership summary used by Phase-3 visualization. The scope key is the
 * first legrecno in the scope (stable across renders for a given dispatch
 * grouping) and the boolean flags drive border-rounding + the "Included" badge.
 */
interface ScopeMembership {
  scopeKey: string;
  isMultiLegScope: boolean;
  isFirstInScope: boolean;
  isLastInScope: boolean;
  scopeIndex: number;
}

function resolveScopeMembership(leg: ProLeg, all: ProLeg[]): ScopeMembership {
  const scope = getDispatchScope(leg.legrecno, all);
  const scopeKey = String(scope[0] ?? leg.legrecno);
  const idx = scope.indexOf(leg.legrecno);
  return {
    scopeKey,
    isMultiLegScope: scope.length > 1,
    isFirstInScope: idx === 0,
    isLastInScope: idx === scope.length - 1,
    scopeIndex: idx,
  };
}

/**
 * Tailwind classes applied to the leg row/card based on scope membership.
 * Multi-leg scopes get a purple border on left + right always, with rounded
 * corners only on the first/last leg so the border visually connects across
 * all legs in the scope. Single-leg scopes fall back to the default styling.
 */
function scopeBorderClass(scope: ScopeMembership, layout: 'row' | 'card'): string {
  if (!scope.isMultiLegScope) {
    return layout === 'card' ? 'border border-slate-200 dark:border-gray-700 rounded-md' : '';
  }
  // Multi-leg: purple border on left + right always, top only on first,
  // bottom only on last. Rounded corners follow the same first/last rule.
  const top = scope.isFirstInScope ? 'border-t-2 rounded-t-md' : '';
  const bottom = scope.isLastInScope ? 'border-b-2 rounded-b-md' : '';
  return `border-l-2 border-r-2 border-purple-500 ${top} ${bottom}`.trim();
}

/** Per-leg state badges (New / Included / In Progress). Rendered next to the
 * leg number in both wide and cards layouts so the visual cues stay consistent
 * across breakpoints. When `vertical` is true (wide layout's narrow 32px #
 * column) the badges flip to a tall, narrow orientation that reads bottom-to-
 * top so they fit without clipping. */
/**
 * Per-leg "changed elsewhere" callout. The pre-save stale check (useOrderEditing)
 * refetches the order, drops the user's edits for any field another user moved, reloads
 * the fresh DB values and aborts the save — but until now it only raised a toast, so the
 * user could not see WHICH leg or field had moved. This is that missing half: one badge
 * per affected leg, naming the fields in its tooltip.
 *
 * Reads straight from LegEditingCtx, so both the wide and card layouts get it by
 * dropping <LegConflictBadge /> next to their existing NEW badge.
 */
const LegConflictBadge: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const { editing, legrecno, legnum } = React.useContext(LegEditingCtx);
  const fields = legConflictFields(editing?.conflicts, legrecno);
  if (fields.length === 0) return null;

  const added = fields.includes('__new__');
  const removed = fields.includes('__removed__');
  const label = added ? 'ADDED' : removed ? 'DELETED' : 'CHANGED';
  const tip = added
    ? 'This leg was added by someone else while you had the order open.'
    : removed
      ? 'This leg was deleted by someone else while you had the order open.'
      : `Changed by someone else while you had the order open: `
        + `${describeLegConflictFields(fields)}. `
        + 'The values shown are from the database — your edits to these fields were '
        + 'dropped. Review and save again.';

  return (
    <Tooltip content={tip} placement="top">
      <span
        className={
          (compact
            ? 'mt-0.5 px-1 py-0 text-[0.5rem] '
            : 'px-1.5 py-0.5 text-[0.5625rem] ')
          + 'font-bold uppercase tracking-wider rounded bg-amber-500 text-white cursor-help'
        }
        data-testid={`leg-conflict-badge-${legnum}`}
      >
        {label}
      </span>
    </Tooltip>
  );
};

const LegStateBadges: React.FC<{
  leg: ProLeg;
  scope: ScopeMembership;
  vertical?: boolean;
}> = ({ leg, scope, vertical = false }) => {
  const isNew = leg.legrecno < 0;
  const showIncluded = scope.isMultiLegScope && !scope.isLastInScope;
  const inProgress = !!leg.origArrivalDateTime && !leg.destDepartDateTime;
  if (!isNew && !showIncluded && !inProgress) return null;
  const orientation = vertical
    ? '[writing-mode:vertical-rl] rotate-180 py-1.5 px-0.5'
    : 'px-1.5 py-0.5';
  const base = `text-[0.625rem] font-semibold uppercase tracking-wide rounded ${orientation}`;
  return (
    <>
      {isNew && (
        <span
          className={`${base} bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300`}
          data-testid={`leg-badge-new-${leg.legnum}`}
        >
          New
        </span>
      )}
      {showIncluded && (
        <span
          className={`${base} bg-slate-200 dark:bg-gray-700 text-slate-700 dark:text-gray-300`}
          data-testid={`leg-badge-included-${leg.legnum}`}
        >
          Included
        </span>
      )}
      {inProgress && (
        <span
          className={`${base} bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300`}
          data-testid={`leg-badge-in-progress-${leg.legnum}`}
        >
          In Progress
        </span>
      )}
    </>
  );
};

/**
 * Whole-leg "apply to all" checkbox — DISABLED for now (per product decision: legs are
 * not Change-All-able). Renders nothing, so no leg is ever flagged for fan-out. The
 * wiring is intentionally left intact to re-enable later without rebuilding it: the
 * context still exposes `checkedLegs`/`toggleLeg` and `ProDetailContainer` still calls
 * `buildLegChangeAllRows` (which simply sees an empty `checkedLegs`). To bring the
 * checkbox back, restore the input below (reading `useChangeAll()`, gated on
 * `enabled && leg.legrecno > 0`, keyed by `String(leg.legrecno)`).
 */
const LegApplyAllCheckbox: React.FC<{ leg: ProLeg }> = () => null;

/** True when this leg is a non-last leg in a multi-leg scope whose driver
 * disagrees with the scope's last-leg driver — including the partial-fanout
 * case where this leg has a driver but the last leg is empty.
 *
 * Dispatch is supposed to atomically fan the driver to every leg in scope,
 * so a non-empty driver on a non-last leg with an empty last-leg driver
 * almost always means a real error (e.g. one leg's assignment was reverted
 * or never propagated). Worth surfacing — the user only sees the last
 * leg's editable cell, which would otherwise read "no driver" with no clue
 * that an earlier leg in the same scope already has one assigned.
 *
 * Diverges from the drawer's parallel check at
 * OrderDetailsModalContent.tsx:3223, which still requires both sides non-
 * empty. We diverge here intentionally per dispatcher feedback. */
function hasDriverMismatch(leg: ProLeg, all: ProLeg[]): boolean {
  if (!all.length) return false;
  const scope = getDispatchScope(leg.legrecno, all);
  if (scope.length < 2) return false;
  const lastLegrecno = scope[scope.length - 1];
  if (lastLegrecno === leg.legrecno) return false;
  const lastLeg = all.find((l) => l.legrecno === lastLegrecno);
  if (!lastLeg) return false;
  const lastDriver = (lastLeg.driver ?? '').trim();
  // Symmetric: read both sides from `all` (effectiveLegs overlay).
  const thisInScope = all.find((l) => l.legrecno === leg.legrecno) ?? leg;
  const thisDriver = (thisInScope.driver ?? '').trim();
  // Only flag legs that themselves have a driver — a non-last leg with no
  // driver has nothing to display in the chip and isn't the source of the
  // mismatch (the user assigns the driver on the scope's last leg).
  if (!thisDriver) return false;
  return thisDriver !== lastDriver;
}

/** Calendar date of a leg date/datetime string as `YYYY-MM-DD`, or null when
 *  it carries no real date. Accepts the grid's display format ("MM/DD/YYYY",
 *  "MM/DD/YYYY HH:MM") and raw ISO ("YYYY-MM-DDTHH:MM:SS") — leg actuals reach
 *  the cells in either shape depending on the source — and treats V1 zero-dates
 *  ("00/00/0000", "0000-00-00") as blank rather than as a date in 1899. */
function legCalendarDate(raw?: string | null): string | null {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  const iso = formatBackendDate(s.split(/[ T]/)[0]);
  if (!iso || iso.startsWith('0000') || iso.includes('-00')) return null;
  return iso;
}

/** True when a stop's appointment date falls on a different calendar day than
 * the driver's own arrival/departure timestamps for that same stop.
 *
 * Dispatch is supposed to move the appointment when the driver's dates move, so
 * a filled actual on another day almost always means the appointment was never
 * updated (ex: PRO 5071196 — appt 8/18 with both driver dates on 8/19). Worth
 * bordering red: the date cell reads perfectly plausible on its own, and only
 * the row beneath it reveals the disagreement.
 *
 * Blank actuals (a stop the driver hasn't worked yet) and a blank appointment
 * date are "nothing to compare", not a mismatch. */
export function hasApptDriverDateMismatch(
  apptDate?: string | null,
  driverDateTimes: (string | null | undefined)[] = [],
): boolean {
  const appt = legCalendarDate(apptDate);
  if (!appt) return false;
  return driverDateTimes.some((v) => {
    const actual = legCalendarDate(v);
    return actual !== null && actual !== appt;
  });
}

/** True when a stop's appointment date is already behind us and the driver has
 * recorded NO arrival/departure time for that stop.
 *
 * The companion rule to hasApptDriverDateMismatch: that one catches a stop the
 * driver worked on a different day than booked; this one catches a stop that
 * was booked, the day came and went, and nothing was ever entered — either the
 * move slipped and the appointment was never rebooked, or the driver times were
 * never keyed. Both read as a perfectly plausible date on their own.
 *
 * Today's appointment is not overdue (the driver may be on the way), and once
 * any driver time exists the day-mismatch rule owns the cell. Blank and V1
 * zero appointment dates are "nothing to compare". `today` is injectable so
 * tests don't drift as the calendar advances. */
export function hasPastApptWithoutDriverTimes(
  apptDate?: string | null,
  driverDateTimes: (string | null | undefined)[] = [],
  today: Date = new Date(),
): boolean {
  const appt = legCalendarDate(apptDate);
  if (!appt) return false;
  if (driverDateTimes.some((v) => legCalendarDate(v) !== null)) return false;
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const d = String(today.getDate()).padStart(2, '0');
  return appt < `${y}-${m}-${d}`;
}

/**
 * Whether a leg's move is finished. `legstatus` is unreliable, so we derive
 * completeness from the driver + the actual arrive/depart timestamps:
 *
 *  - Outside carrier — driver code "000" or any code containing a letter: we
 *    don't capture full arrive/depart actuals for these, so the leg is complete
 *    once it has arrived at its destination (destArrivalDateTime is set).
 *  - GWI driver — a purely-numeric code (001-99999): all four actuals must be
 *    filled — origin arrive + depart and destination arrive + depart.
 *
 * A leg with no driver assigned is never complete.
 */
export function isLegComplete(leg: ProLeg): boolean {
  const driver = (leg.driver ?? '').trim();
  if (!driver) return false;

  const hasTime = (v?: string | null): boolean => !!(v && String(v).trim());
  const isOutsideCarrier = driver === '000' || /[a-z]/i.test(driver);

  if (isOutsideCarrier) {
    return hasTime(leg.destArrivalDateTime);
  }
  return (
    hasTime(leg.origArrivalDateTime) &&
    hasTime(leg.origDepartDateTime) &&
    hasTime(leg.destArrivalDateTime) &&
    hasTime(leg.destDepartDateTime)
  );
}

/** Build the leg-context payload that drives the POST validate-driver path. */
function buildLegContextForValidation(
  legs: ProLeg[],
): DriverValidationLegContextItem[] | null {
  if (!legs.length) return null;
  return legs.map((leg) => ({
    legnum: leg.legnum,
    legdrop: ((leg.legDrop || 'N') as 'Y' | 'N'),
    destapptdate: formatBackendDate(leg.destApptDate ?? null),
    origcode: leg.origcode != null ? Number(leg.origcode) : null,
    destcode: leg.destcode != null ? Number(leg.destcode) : null,
  }));
}

/**
 * Build the `order_overrides` payload that flows through to the POST
 * validate-driver endpoint so qualification reflects unsaved order-level
 * edits. Keys must match the backend's lowercase column names (see
 * driver_dispatch.get_order_details + check_driver_qualification — they read
 * `order.get('overweightpermitrequired')`, NOT camelCase).
 *
 * Only forwards fields that affect driver qualification today. The shape is
 * a whitelist rather than a blanket "send everything edited" because most
 * edited fields are irrelevant to qualification and an unfiltered dump would
 * be noisy in the [OW_QUAL_DEBUG] logs.
 */
function buildOrderOverridesForValidation(
  editedOrderFields: Record<string, unknown> | undefined,
): Record<string, unknown> | null {
  if (!editedOrderFields) return null;
  const overrides: Record<string, unknown> = {};
  // camelCase (editedOrderFields) → lowercase (backend order dict)
  const map: Record<string, string> = {
    overweightPermitRequired: 'overweightpermitrequired',
    overweightApprovedBy: 'overweightapprovedby',
    overweightApprovedDatetime: 'overweightapproveddatetime',
    chassisType: 'chassistype',
    equipType: 'equiptype',
    size: 'size',
    isTank: 'istank',
    isDaycab: 'isdaycab',
  };
  for (const [camel, lower] of Object.entries(map)) {
    if (editedOrderFields[camel] !== undefined) {
      overrides[lower] = editedOrderFields[camel];
    }
  }
  return Object.keys(overrides).length > 0 ? overrides : null;
}

// ---------------------------------------------------------------------------
// Editing context — cells reach into `editing` without prop-drilling.
// ---------------------------------------------------------------------------

interface LegEditingCtxValue {
  editing?: OrderEditingProp;
  legrecno: number;
  /** This leg's legnum — used by LegDateCell to detect "this is the order's
   *  delivery leg" and reroute destApptDate edits through the required
   *  reschedule modal. */
  legnum?: number;
  isPendingRemoved: boolean;
  isNew: boolean;
  /** Leg has departed its destination — appt date/time cells render
   *  read-only bold text instead of editable date/time pickers. */
  isComplete?: boolean;
}

const LegEditingCtx = React.createContext<LegEditingCtxValue>({
  editing: undefined,
  legrecno: 0,
  legnum: undefined,
  isPendingRemoved: false,
  isNew: false,
  isComplete: false,
});

// ---------------------------------------------------------------------------
// Hover context — cells highlight when their `field` matches the hovered key.
// ---------------------------------------------------------------------------

type HoveredField = string | null;
interface HoverCtxValue {
  hField: HoveredField;
  setHField: (f: HoveredField) => void;
}
const HoverCtx = React.createContext<HoverCtxValue>({
  hField: null,
  setHField: () => {},
});

// ---------------------------------------------------------------------------
// Travel-history modal context — lets LegRow/LegsCards trigger a single
// modal owned by LegsGrid without prop-drilling through every leg component.
// ---------------------------------------------------------------------------

interface TravelHistoryCtxValue {
  openTravelHistory: (legNum: number) => void;
}
const TravelHistoryCtx = React.createContext<TravelHistoryCtxValue>({
  openTravelHistory: () => {},
});

interface LegTravelHistoryButtonProps {
  leg: ProLeg;
}

const LegTravelHistoryButton: React.FC<LegTravelHistoryButtonProps> = ({ leg }) => {
  const { openTravelHistory } = React.useContext(TravelHistoryCtx);
  const driver = (leg.driver ?? '').trim();
  if (!driver) return null;
  return (
    <button
      type="button"
      onClick={() => openTravelHistory(leg.legnum)}
      className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-slate-100 dark:hover:bg-gray-700"
      aria-label={`Leg ${leg.legnum} travel history`}
      data-testid={`leg-travel-history-${leg.legnum}`}
    >
      <MapPinIcon className="w-3.5 h-3.5 text-slate-600 dark:text-gray-300" />
    </button>
  );
};

// ---------------------------------------------------------------------------
// Dispatch-info modal context — lets LegRow/LegsCards trigger a single
// modal owned by LegsGrid without prop-drilling through every leg component.
// ---------------------------------------------------------------------------

interface DispatchInfoCtxValue {
  openDispatchInfo: (leg: ProLeg) => void;
}
const DispatchInfoCtx = React.createContext<DispatchInfoCtxValue>({
  openDispatchInfo: () => {},
});

interface LegDispatchInfoButtonProps {
  leg: ProLeg;
  /** Show the permissions cog on only one instance (the first leg), matching LegDriverCell. */
  showCog?: boolean;
}

const LegDispatchInfoButton: React.FC<LegDispatchInfoButtonProps> = ({ leg, showCog = false }) => {
  const { openDispatchInfo } = React.useContext(DispatchInfoCtx);
  // Gate the whole Dispatch Details modal (open + its editable controls) on the new
  // `leg-dispatch-info-edit` permission — reproduces v1's `view_driverstatusinfo` open gate.
  // At level 0 the icon is hidden; at level 1 it opens read-only; level 2 enables edits.
  return (
    <PermissionsContainer
      componentId="leg-dispatch-info-edit"
      componentName="Dispatch Info"
      showCog={showCog}
    >
      <button
        type="button"
        onClick={() => openDispatchInfo(leg)}
        className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-slate-100 dark:hover:bg-gray-700"
        aria-label={`Leg ${leg.legnum} dispatch info`}
        data-testid={`leg-dispatch-info-${leg.legnum}`}
      >
        <svg className="w-3.5 h-3.5 text-slate-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>
    </PermissionsContainer>
  );
};

// ---------------------------------------------------------------------------
// Send-dispatch modal context — lets the row icon button open a confirmation
// modal owned by LegsGrid (where `pronumber` is in scope).
// ---------------------------------------------------------------------------

interface SendDispatchCtxValue {
  openSendDispatch: (leg: ProLeg) => void;
}
const SendDispatchCtx = React.createContext<SendDispatchCtxValue>({
  openSendDispatch: () => {},
});

// ---------------------------------------------------------------------------
// Send-dispatch action — opens a menu next to the row icons with two actions:
// "Send Paperwork To Driver" (analog of v1 dap/dispatch.pl sendDispatch email
// path) and "Download Paperwork As PDF". Menu is portaled because the leg
// row sits inside an overflow-hidden card.
// ---------------------------------------------------------------------------

interface LegSendDispatchButtonProps {
  leg: ProLeg;
}

const LegSendDispatchButton: React.FC<LegSendDispatchButtonProps> = ({ leg }) => {
  const { openSendDispatch } = React.useContext(SendDispatchCtx);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const MENU_WIDTH = 224;
  const MENU_HEIGHT = 108;
  const MARGIN = 8;
  // Print → CUPS (V1 driverdispatch_print.pl). The dialog is mounted here, not
  // inside the menu, so it survives the menu closing on the click that opens it.
  const [printOpen, setPrintOpen] = useState(false);

  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    let top = rect.bottom + 4;
    let left = rect.left;
    if (left + MENU_WIDTH > window.innerWidth - MARGIN) {
      left = Math.max(MARGIN, window.innerWidth - MENU_WIDTH - MARGIN);
    }
    if (top + MENU_HEIGHT > window.innerHeight - MARGIN) {
      top = Math.max(MARGIN, rect.top - MENU_HEIGHT - 4);
    }
    setMenuPos({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleSendToDriver = () => {
    setOpen(false);
    const dispatchRecno = leg.dispatchid ?? 0;
    if (!dispatchRecno) {
      alert('This leg is not dispatched yet — assign a driver before sending paperwork.');
      return;
    }
    openSendDispatch(leg);
  };

  const handleDownloadPdf = async () => {
    setOpen(false);
    const dispatchRecno = leg.dispatchid ?? 0;
    if (!dispatchRecno) {
      alert('This leg is not dispatched yet — assign a driver before downloading paperwork.');
      return;
    }
    try {
      await saveAs(`dispatch_${dispatchRecno}.pdf`, () =>
        dispatchPaperworkService.downloadBlob(dispatchRecno),
      );
    } catch (err) {
      console.error('Failed to download dispatch paperwork', err);
      alert('Failed to download dispatch paperwork. Check the console for details.');
    }
  };

  const handlePrint = () => {
    setOpen(false);
    if (!(leg.dispatchid ?? 0)) {
      alert('This leg is not dispatched yet — assign a driver before printing paperwork.');
      return;
    }
    setPrintOpen(true);
  };

  const printPaperwork = (opts: Required<PrintOptions>) =>
    printingService.printTo(`/driver-dispatch/dispatch-paperwork/${leg.dispatchid ?? 0}/print`, opts);

  return (
    <>
      {/* Mounted only while open: one dialog per leg row would otherwise subscribe
          every row to the print-defaults fetch and the toast context. */}
      {printOpen && (
        <PrintDialog
          open
          type="LASER"
          title="Print Paperwork"
          onClose={() => setPrintOpen(false)}
          onPrint={printPaperwork}
        />
      )}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-slate-100 dark:hover:bg-gray-700"
        aria-label={`Leg ${leg.legnum} send dispatch`}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`leg-send-dispatch-${leg.legnum}`}
      >
        <PaperAirplaneIcon className="w-3.5 h-3.5 text-slate-600 dark:text-gray-300" />
      </button>
      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, width: MENU_WIDTH }}
          className="z-[9999] rounded-md border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg py-1 text-sm text-slate-700 dark:text-gray-300"
        >
          <button
            type="button"
            role="menuitem"
            onClick={handleSendToDriver}
            className="block w-full text-left px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-gray-700"
            data-testid={`leg-send-dispatch-${leg.legnum}-email`}
          >
            Send Paperwork To Driver
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleDownloadPdf}
            className="block w-full text-left px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-gray-700"
            data-testid={`leg-send-dispatch-${leg.legnum}-download`}
          >
            Download Paperwork As PDF
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handlePrint}
            className="block w-full text-left px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-gray-700"
            data-testid={`leg-send-dispatch-${leg.legnum}-print`}
          >
            Print Paperwork
          </button>
        </div>,
        document.body,
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Add-stopoff splitter — vertical (or horizontal in stacked/cards layouts)
// affordance between Origin and Destination. Clicking it splits the current
// leg at a stopoff via the editing hook's mid-position addLeg, which:
//   - keeps this leg's origin as-is and clears its destination side
//   - inserts a new leg whose origin is the stopoff and whose destination
//     inherits from this leg's original destination
// ---------------------------------------------------------------------------

interface LegSplitterButtonProps {
  leg: ProLeg;
  /** When true, render a horizontal splitter (full-width row, line above + below
   * the +) — used by the stacked layout where origin and destination are
   * vertically stacked. Default is vertical (line top-to-bottom). */
  orientation?: 'vertical' | 'horizontal';
}

const LegSplitterButton: React.FC<LegSplitterButtonProps> = ({
  leg,
  orientation = 'vertical',
}) => {
  const { editing, isPendingRemoved } = React.useContext(LegEditingCtx);
  const { legAddBlock } = React.useContext(OrderContextCtx);
  const disabled = !editing?.addLeg || isPendingRemoved || !!legAddBlock;
  const handleClick = () => {
    if (disabled) return;
    // `mid` takes a legrecno target (stable across the renumbering done by
    // effectiveLegs). `leg.legnum` here is the display index, which doesn't
    // line up with the hook's internal positions once pending-new legs exist.
    editing?.addLeg?.('mid', leg.legrecno);
  };
  // Drawer parity (OrderDetailsModalContent.tsx ~3515): splitting a paid leg
  // creates a new back-half leg whose origin can't be removed (the paid front
  // half blocks `canRemoveOriginStop`), stranding the inserted stop. Only the
  // BUTTON is suppressed (paid leg or hidden permission) — the structural
  // wrapper below must always render, because the cards layout places this
  // component in the 24px middle track of an auto-placed '1fr 24px 1fr' grid.
  // Returning null there shifts the destination LegEnd into the 24px track
  // and crushes it.
  const button = leg.isPaid ? null : (
    <PermissionsContainer
      componentId="leg-add-remove-stops"
      componentName="Add/Remove Stops"
      showCog={false}
      defaultHidden={true}
    >
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        title={legAddBlock ?? undefined}
        aria-label={`Add stopoff after leg ${leg.legnum} origin`}
        data-testid={`leg-add-stopoff-${leg.legnum}`}
        className={
          'relative z-10 w-5 h-5 inline-flex items-center justify-center rounded-full border ' +
          'text-[0.75rem] font-bold leading-none ' +
          (disabled
            ? 'bg-slate-100 dark:bg-gray-800 text-slate-300 dark:text-gray-500 border-slate-200 dark:border-gray-700 cursor-not-allowed'
            : 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 border-slate-300 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:border-blue-400 hover:text-blue-700 dark:hover:text-blue-300 cursor-pointer')
        }
      >
        +
      </button>
    </PermissionsContainer>
  );
  return orientation === 'horizontal' ? (
    <div
      className="relative flex items-center justify-center py-1 select-none"
      data-testid={`leg-splitter-slot-${leg.legnum}`}
    >
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-slate-300 dark:bg-gray-600" />
      {button}
    </div>
  ) : (
    <div
      className="relative flex flex-col items-center justify-center h-full w-full select-none"
      data-testid={`leg-splitter-slot-${leg.legnum}`}
    >
      <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-slate-300 dark:bg-gray-600" />
      {button}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Clear-driver confirmation context — lets LegDriverCell signal that the user
// attempted a destructive driver-clear (scope has timestamps), so the single
// confirmation modal owned by LegsGrid can collect Confirm/Cancel input.
// ---------------------------------------------------------------------------

interface ClearDriverCtxValue {
  requestClearDriver: (legrecno: number, revert: () => void) => void;
}
const ClearDriverCtx = React.createContext<ClearDriverCtxValue>({
  requestClearDriver: () => {},
});

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Cols: 1 # | 2-5 Origin (MoveType / Appt# / Date / Time) | 6 Splitter |
//       7-10 Destination (MoveType / Appt# / Date / Time) | 11 Details | 12 ×
const GRID_TEMPLATE =
  '32px 1.0fr 0.7fr 1.1fr 1.0fr 24px 1.0fr 0.7fr 1.1fr 1.0fr 1.2fr 28px';

// Per-leg mile-allocated margin is hidden for now (the "Margin (based on miles)"
// cell). Flip to true to re-surface it in both the wide-grid and cards layouts;
// the plumbing (marginByLeg / marginLimit / LegMarginCell) stays wired so this
// is a one-line re-enable. The per-Pro total margin (header pill + modal) is
// unaffected.
const SHOW_LEG_MARGIN: boolean = false;

// Move-type set mirrors OrderDetailsModalContent (the drawer). Leading '' is
// the clear-field option, rendered as '-' in the dropdown.
const MOVE_TYPE_OPTIONS = [
  '',
  'ADD PRODUCT',
  'CHASSIS SPLIT',
  'DELIVERY',
  'GROUNDED',
  'PICKUP',
  'REJECTED',
  'REMOVE PRODUCT',
  'REWORK',
  'SCALE',
  'STOPOFF',
  'STORAGE',
  'TERMINATE',
] as const;

// Move-type badge classes mirroring the prototype's pickup/delivery/terminate
// pill colors.
function moveTypeBadge(legtype: string | null | undefined): string {
  const t = (legtype || '').toUpperCase();
  if (t.includes('PICKUP') || t.includes('PICK'))
    return 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
  if (t.includes('DELIVER') || t.includes('DROP'))
    return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
  if (t.includes('TERMINATE') || t.includes('TERM'))
    return 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300';
  if (t.includes('EMPTY')) return 'bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-300';
  if (t.includes('RAIL')) return 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300';
  return 'bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-300';
}

/** Lowercase the whole memo, then uppercase the first letter of each
 * sentence. Memos are stored in the legacy CMS as ALL CAPS — sentence
 * case reads better in tooltips. */
function toSentenceCase(s: string | null | undefined): string {
  if (!s) return '';
  return s
    .toLowerCase()
    .replace(/(^|[.!?]\s+)([a-z])/g, (_m, sep, c) => sep + c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Reusable cells
// ---------------------------------------------------------------------------

const CELL_INPUT_BASE =
  'w-full h-5 px-1 py-0 text-[0.6875rem] rounded border border-slate-300 dark:border-gray-600 ' +
  'bg-white dark:bg-gray-900 text-slate-900 dark:text-gray-100 box-border ' +
  'focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20';

interface CIProps {
  /** Field name on `OrderLeg` — when set, the cell calls
   * `editing.updateLegField(legrecno, field, value)` on blur. */
  field?: string;
  value?: string | number | null;
  mono?: boolean;
  placeholder?: string;
  color?: string;
  ariaLabel?: string;
  /** Per-field gate from `legFieldGuards`. When false, the input is disabled
   * (rendered alongside any wrapping PermissionsContainer's own disable). */
  disabled?: boolean;
}

const CI: React.FC<CIProps> = ({ field, value, mono, placeholder, color, ariaLabel, disabled }) => {
  const { editing, legrecno, isPendingRemoved } = React.useContext(LegEditingCtx);
  const isDisabled = !!disabled || isPendingRemoved;

  // Prefer the edited value (if any) over the original. This makes the input
  // a controlled mirror of the editing-hook state — typing then clicking
  // away will not snap back to the server value.
  const editedRaw =
    field != null && editing?.editedLegs?.[legrecno]
      ? (editing.editedLegs[legrecno] as Record<string, unknown>)[field]
      : undefined;
  const sourceValue: unknown = editedRaw !== undefined ? editedRaw : value;

  const [v, setV] = useState<string>(sourceValue == null ? '' : String(sourceValue));

  // Resync when the upstream value changes (e.g. the edited value was wiped
  // by a save / cancel). Tracking the stringified upstream avoids stomping
  // mid-edit changes that don't change the underlying source.
  useEffect(() => {
    setV(sourceValue == null ? '' : String(sourceValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceValue]);

  // Stale-field highlight: when the hook detected an external change to this
  // field while the user had a pending edit, render an amber ring + bg.
  const stale =
    field != null && !!editing?.staleFields?.[legrecno]?.includes(field);
  const clearStale = () => {
    if (stale && field != null) editing?.clearStaleField?.(legrecno, field);
  };

  const handleBlur = () => {
    clearStale();
    if (!field || !editing?.updateLegField) return;
    const original = value == null ? '' : String(value);
    if (v === original) return; // no-op: matches the server value
    editing.updateLegField(legrecno, field, v);
  };

  return (
    <input
      value={v}
      placeholder={placeholder ?? ''}
      onChange={(e) => {
        setV(e.target.value);
        clearStale();
      }}
      onFocus={clearStale}
      onBlur={handleBlur}
      aria-label={ariaLabel}
      disabled={isDisabled}
      data-stale={stale ? 'true' : undefined}
      style={color ? { color } : undefined}
      className={`${CELL_INPUT_BASE} ${stale ? 'ring-2 ring-amber-400 bg-amber-50 dark:bg-amber-900/30' : ''} ${isDisabled ? 'cursor-not-allowed bg-slate-50 dark:bg-gray-800 text-slate-500 dark:text-gray-400' : ''}`}
    />
  );
};

interface CSProps {
  field?: string;
  value?: string | null;
  options: ReadonlyArray<string>;
  badgeClass?: string;
  width?: number;
  mono?: boolean;
  ariaLabel?: string;
  /** Per-field gate from `legFieldGuards`. */
  disabled?: boolean;
}

const CS: React.FC<CSProps> = ({
  field,
  value,
  options,
  badgeClass,
  width,
  mono,
  ariaLabel,
  disabled,
}) => {
  const { editing, legrecno, isPendingRemoved } = React.useContext(LegEditingCtx);
  const isDisabled = !!disabled || isPendingRemoved;

  const editedRaw =
    field != null && editing?.editedLegs?.[legrecno]
      ? (editing.editedLegs[legrecno] as Record<string, unknown>)[field]
      : undefined;
  const sourceValue: unknown = editedRaw !== undefined ? editedRaw : value;

  const [v, setV] = useState<string>(sourceValue == null ? '' : String(sourceValue));
  useEffect(() => {
    setV(sourceValue == null ? '' : String(sourceValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceValue]);

  // Make sure the current value is selectable even if it isn't in `options`.
  const opts = useMemo(
    () => (v && !options.includes(v) ? [v, ...options] : options),
    [v, options],
  );

  // Stale-field highlight (amber ring) — see CI for details.
  const stale =
    field != null && !!editing?.staleFields?.[legrecno]?.includes(field);
  const clearStale = () => {
    if (stale && field != null) editing?.clearStaleField?.(legrecno, field);
  };

  const handleChange = (next: string) => {
    setV(next);
    clearStale();
    if (!field || !editing?.updateLegField) return;
    editing.updateLegField(legrecno, field, next);
  };

  return (
    <select
      value={v}
      onChange={(e) => handleChange(e.target.value)}
      onFocus={clearStale}
      aria-label={ariaLabel}
      disabled={isDisabled}
      data-stale={stale ? 'true' : undefined}
      style={width ? { width } : undefined}
      className={`${CELL_INPUT_BASE} ${stale ? 'ring-2 ring-amber-400 bg-amber-50 dark:bg-amber-900/30' : ''} ${isDisabled ? 'cursor-not-allowed bg-slate-50 dark:bg-gray-800 text-slate-500 dark:text-gray-400' : 'cursor-pointer'} pr-1 ${badgeClass ?? ''}`}
    >
      {opts.map((o) => (
        <option key={o} value={o}>
          {o === '' ? '-' : o}
        </option>
      ))}
    </select>
  );
};

// ---------------------------------------------------------------------------
// Inline "remove stop" button. Mirrors the drawer at
// OrderDetailsModalContent.tsx ~3364-3375 and ~3621-3631: a small grey minus
// pill rendered next to the origin name on legs after the first, and next to
// the destination name on the last leg only. The user always sees one button
// per physical stop in the chain; `side` picks which side of the leg gets
// the click. Hook handles the merge for interior origin removal.
// ---------------------------------------------------------------------------

interface RemoveStopButtonProps {
  side: 'origin' | 'destination';
  legnum: number;
  /** When true, hide the button entirely (e.g. paid leg, DELIVERY boundary,
   * single-leg order). Mirrors the drawer's conditional render. */
  hidden?: boolean;
}

const RemoveStopButton: React.FC<RemoveStopButtonProps> = ({ side, legnum, hidden }) => {
  const { editing, legrecno, isPendingRemoved } = React.useContext(LegEditingCtx);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 3000);
    return () => clearTimeout(t);
  }, [error]);
  if (hidden || isPendingRemoved || !editing?.removeLeg) return null;
  return (
    <div className="relative flex items-center justify-center">
      <button
        type="button"
        onClick={() => {
          const r = editing.removeLeg!(legrecno, side);
          if (!r.ok) setError(r.reason ?? 'Cannot remove stop');
        }}
        title="Remove stop"
        aria-label={`Remove leg ${legnum} ${side} stop`}
        data-testid={`leg-remove-stop-${legnum}-${side}`}
        className={
          'flex-shrink-0 w-4 h-4 inline-flex items-center justify-center rounded-full ' +
          (error
            ? 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 ring-1 ring-rose-300 dark:ring-rose-800'
            : 'bg-slate-300 dark:bg-gray-600 hover:bg-rose-600 text-white transition-colors')
        }
      >
        <svg viewBox="0 0 16 16" fill="currentColor" className="w-2.5 h-2.5" aria-hidden="true">
          <path d="M3.25 8a.75.75 0 0 1 .75-.75h8a.75.75 0 0 1 0 1.5H4a.75.75 0 0 1-.75-.75Z" />
        </svg>
      </button>
      {error && (
        <div
          role="alert"
          className="absolute right-full top-1/2 -translate-y-1/2 mr-1 whitespace-nowrap text-[0.625rem] font-medium text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 px-1.5 py-0.5 rounded shadow-sm z-10"
          data-testid={`leg-remove-stop-${legnum}-${side}-error`}
        >
          {error}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Live/Drop toggle badge. Mirrors the drawer's pill at
// OrderDetailsModalContent.tsx ~3546-3580: green "Drop" when legDrop='Y',
// blue "Live" when 'N', chevron rotates 180° on Drop. Clicking toggles via
// editing.updateLegField; legDrop cascade handling lives in the hook.
// ---------------------------------------------------------------------------

const DropToggleBadge: React.FC<{ legDrop: string | null | undefined; disabled?: boolean }> = ({
  legDrop,
  disabled,
}) => {
  const { editing, legrecno, isPendingRemoved } = React.useContext(LegEditingCtx);
  const isDisabled = !!disabled || isPendingRemoved || !editing?.updateLegField;
  const isDrop = legDrop === 'Y';
  return (
    <button
      type="button"
      onClick={() => {
        if (isDisabled) return;
        editing!.updateLegField!(legrecno, 'legDrop', isDrop ? 'N' : 'Y');
      }}
      disabled={isDisabled}
      aria-label={`Toggle drop (currently ${isDrop ? 'Drop' : 'Live'})`}
      title={isDisabled ? 'View only' : `Click to toggle drop (currently ${isDrop ? 'Drop' : 'Live'})`}
      className={
        'inline-flex items-center gap-0.5 px-1 h-5 rounded border text-[0.6875rem] font-medium transition-colors ' +
        (isDisabled ? 'cursor-not-allowed opacity-60 ' : 'cursor-pointer ') +
        (isDrop
          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700 ' +
            (isDisabled ? '' : 'hover:bg-green-200 dark:hover:bg-green-900/40 hover:border-green-500')
          : 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-700 ' +
            (isDisabled ? '' : 'hover:bg-blue-200 dark:hover:bg-blue-900/40 hover:border-blue-500'))
      }
    >
      {isDrop ? 'Drop' : 'Live'}
      <svg
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
        className={`w-2.5 h-2.5 transition-transform ${isDrop ? 'rotate-180' : ''}`}
      >
        <path
          fillRule="evenodd"
          d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
          clipRule="evenodd"
        />
      </svg>
    </button>
  );
};

// ---------------------------------------------------------------------------
// Date / Time / DateTime cells. Wrap the shared DateTimePicker for the appt
// date, appt time, and arrival/depart datetime fields. String formats mirror
// the drawer's:
//   - dates as MM/DD/YYYY
//   - times as HH:MM (24h)
//   - datetimes as "MM/DD/YYYY HH:MM"
// Each cell pulls editing + legrecno from LegEditingCtx, same as CI/CS.
// String <-> Date conversion helpers live in utils/dateUtils for reuse.
// ---------------------------------------------------------------------------

interface DateTimeCellProps {
  field: string;
  value?: string | null;
  disabled?: boolean;
  ariaLabel?: string;
}

interface LegApptDateCellProps extends DateTimeCellProps {
  /** Enables the clear (X) button. Resolved by resolveLegGates via canClearAppt. */
  canClear?: boolean;
  /** True when the PREVIOUS leg is the delivery leg and is LIVE (legDrop !== 'Y').
   *  On a live delivery this cell's origApptDate is the same physical date as the
   *  delivery leg's destApptDate (the cascade keeps them identical), so clearing
   *  here must record the same reschedule row. False on drop deliveries, where
   *  the two dates are genuinely independent. */
  prevLegIsLiveDelivery?: boolean;
  /** aria-label for the clear button. Spelled out by the caller rather than
   *  derived from `ariaLabel`, because "Clear <ariaLabel>" would contain the
   *  cell's own label as a substring and make every existing
   *  getByLabelText(/Leg N dest date/) query ambiguous. */
  clearAriaLabel?: string;
  /** Appt date disagrees with this stop's driver arrival/departure dates — see
   *  hasApptDriverDateMismatch. Borders the picker red. Callers compute it from
   *  the effective (edit-overlaid) leg so the border clears as soon as the user
   *  picks a matching date. */
  apptMismatch?: boolean;
  /** Appt date is in the past and this stop has no driver arrival/departure
   *  time at all — see hasPastApptWithoutDriverTimes. Same red treatment as
   *  `apptMismatch`, different tooltip. */
  apptPastDue?: boolean;
}

// Group wrapper assigns an aria-label for `screen.getByLabelText(...)` access in
// tests (the underlying DateTimePicker doesn't accept aria-label). The wrapped
// <input disabled> is still the element to query for `toBeDisabled()`.
const LegDateCell: React.FC<LegApptDateCellProps> = ({
  field,
  value,
  disabled,
  ariaLabel,
  canClear,
  prevLegIsLiveDelivery,
  clearAriaLabel,
  apptMismatch,
  apptPastDue,
}) => {
  const apptFlagged = !!apptMismatch || !!apptPastDue;
  const { editing, legrecno, legnum, isPendingRemoved, isComplete } = React.useContext(LegEditingCtx);
  const { active: adminOverrideActive } = useAdminOverride();
  const { pronumber, billTo, deliveryLeg, allLegs } = React.useContext(OrderContextCtx);
  // The delivery's ON-SCREEN legnum. `deliveryLeg` is the order's SAVED number,
  // and it goes stale the moment a stop is staged ahead of the delivery: the
  // grid renumbers 1..n while the saved value does not. Every gate below
  // compares an EFFECTIVE legnum, so each one has to judge against an effective
  // anchor — resolved from the same list it walks. Same resolver the
  // drop-release warning uses.
  const effectiveDeliveryLeg = resolveDeliveryLegnum(allLegs, deliveryLeg);
  const isDisabled = !!disabled || isPendingRemoved || !editing?.updateLegField;
  const editedRaw = editing?.editedLegs?.[legrecno]
    ? (editing.editedLegs[legrecno] as Record<string, unknown>)[field]
    : undefined;
  const sourceValue: string =
    editedRaw !== undefined ? String(editedRaw ?? '') : String(value ?? '');

  // Pro page only: changing OR clearing a delivery appointment triggers a
  // required reschedule modal that captures the old → new transition.
  // Modal save POSTs the reschedule immediately + applies the leg edit.
  // Cancelling abandons the change: the leg edit is never staged, so the
  // appointment can only move with a reschedule row behind it.
  //
  // One of TWO mandatory gates on this page, holding the same contract: you cannot
  // leave the modal with the triggering change staged and no record behind it. The
  // other is the REJECTED trouble ticket (#33683, useOrderEditing.updateLegField).
  // They differ only in mechanism, and the widget is what picks it. Here the write is
  // DEFERRED — nothing is staged until onSaved — because DateTimePicker keeps the
  // user's pick in its own state, which is also why cancelling needs `pickerNonce` to
  // remount it. The Move Type <select> mirrors the staged value instead, so that gate
  // stages the edit and reverts to a snapshot. Deferring is right here for a second
  // reason: on a clear this fans out to the downstream legs, and not running that plan
  // beats running it and rolling it back.
  //
  // Two cells reach the same delivery appointment:
  //   1. the delivery leg's own destApptDate, and
  //   2. on a LIVE delivery, the NEXT leg's origApptDate — same physical date,
  //      kept identical by the updateLegField cascade.
  // Only one modal ever fires per boundary: the partner cell is blanked by the
  // cascade inside updateLegField, not by a second LegDateCell.
  const isDeliveryApptEdit =
    !!pronumber &&
    effectiveDeliveryLeg !== undefined &&
    ((field === 'destApptDate' &&
      legnum !== undefined &&
      legnum === effectiveDeliveryLeg) ||
      (field === 'origApptDate' && !!prevLegIsLiveDelivery));
  const [pendingChange, setPendingChange] = useState<{
    oldIso: string | null;
    newIso: string | null;
    /** Picked mm/dd/yyyy — applied to the leg edit when the modal saves. */
    newMdY: string;
    /** Delivery-appt CLEAR only: the legs after the delivery leg whose appt
     *  dates go with it (and the ones that don't). Null for a date CHANGE —
     *  moving an appointment reschedules the downstream legs rather than
     *  cancelling them, so nothing fans out. */
    downstream: DownstreamApptClearPlan | null;
  } | null>(null);
  /** Bumped every time the reschedule modal closes, to remount the picker.
   *  DateTimePicker keeps the user's pick in its own state, and on the cancel
   *  path we deliberately never staged it — so without a remount the cell would
   *  keep displaying a date the order doesn't hold. Remounting re-reads the
   *  effective value: the pre-edit date on cancel, the new one on save (onSaved
   *  stages it before this fires). */
  const [pickerNonce, setPickerNonce] = useState(0);

  // Completed legs are read-only — show the appt date as bold text.
  // Admin Override (#33501) reaches the COMPLETE lock too. `isLegComplete` is a third
  // lock family, separate from isPaid and from the appt-lock scope: driver assigned plus
  // every arrival/departure timestamp filled. It is also precisely the state the override
  // exists for — a finished move whose appointment was recorded wrong — and because this
  // early return happens BEFORE `disabled` is read, releasing the guards alone left these
  // cells stubbornly read-only.
  if (isComplete && !adminOverrideActive) {
    return (
      <span
        aria-label={ariaLabel}
        className="inline-flex items-center h-5 text-[0.6875rem] font-bold text-slate-900 dark:text-gray-100 w-full"
      >
        {sourceValue.trim() || '—'}
      </span>
    );
  }

  const handleChange = (v: Date | null) => {
    const formatted = formatMdYDate(v as Date | null);
    if (isDeliveryApptEdit) {
      // Look up the ORIGINAL saved destApptDate (pre-edit overlay). The
      // `value` prop is already overlaid via editing.effectiveLegs, so we
      // can't use it to detect "first-time set" — a user setting blank →
      // today → tomorrow would otherwise trigger a reschedule on the
      // second pick because the effective value already shows today.
      const originalLeg = editing?.originalLegs?.find((l) => l.legrecno === legrecno);
      const originalRaw = originalLeg
        ? ((originalLeg as unknown as Record<string, unknown>)[field] as string | undefined)
        : undefined;
      const savedDate = parseMdYDate(originalRaw ?? '');
      const sameDayAsSaved =
        (v === null && savedDate === null) ||
        (v !== null &&
          savedDate !== null &&
          v.getFullYear() === savedDate.getFullYear() &&
          v.getMonth() === savedDate.getMonth() &&
          v.getDate() === savedDate.getDate());
      if (sameDayAsSaved) {
        // Net no-op vs. saved state — clear any staged edit so the form
        // doesn't show a phantom dirty cell.
        editing?.updateLegField?.(legrecno, field, formatted);
        return;
      }
      // First-time appointment set (no prior SAVED date) isn't a
      // reschedule — apply the leg edit like a normal field change.
      if (savedDate === null) {
        editing?.updateLegField?.(legrecno, field, formatted);
        return;
      }
      // Clearing the delivery appointment takes the appointment dates on the
      // legs after it too — otherwise the pro bounces straight back onto the
      // LC board via the "Missing Appointment" bucket, which fires whenever an
      // earlier leg is missing a date while a later leg still has one. Only on
      // a clear: a date CHANGE leaves the downstream schedule to be re-planned.
      // Anchored on the SAME list the plan walks — pending-removed legs filtered
      // out — rather than on the grid's. A delivery staged for removal must not
      // anchor a fan-out over a list it is no longer in.
      // `?? allLegs`, not `?? []`: ProDetail always supplies effectiveLegs, but
      // an absent one used to make the plan silently empty — a fan-out that
      // quietly does nothing is worse than one anchored on the grid's own legs.
      const planLegs = (editing?.effectiveLegs ?? allLegs).filter(
        (l) => !editing?.pendingRemovedLegrecnos?.includes(l.legrecno),
      );
      const planDeliveryLeg = resolveDeliveryLegnum(planLegs, deliveryLeg);
      const downstream =
        !formatted && planDeliveryLeg !== undefined
          ? planDownstreamApptClears({
              allLegs: planLegs,
              deliveryLeg: planDeliveryLeg,
            })
          : null;
      setPendingChange({
        oldIso: formatBackendDate(originalRaw ?? ''),
        newIso: formatBackendDate(formatted),
        newMdY: formatted,
        downstream,
      });
      return;
    }
    editing?.updateLegField?.(legrecno, field, formatted);
  };

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      // Red border + light red fill when the appointment day disagrees with the
      // driver's own dates for this stop, or when it has passed with no driver
      // time entered at all. Recolors the picker's existing 1px border and
      // background rather than adding a ring, so nothing shifts in the dense
      // grid. The inner <input> is bg-transparent, so the fill shows through
      // behind the date text.
      data-appt-mismatch={apptMismatch ? 'true' : undefined}
      data-appt-past-due={apptPastDue ? 'true' : undefined}
      title={
        apptMismatch
          ? "Appt date doesn't match the driver's arrival/departure date for this stop"
          : apptPastDue
            ? 'Appt date is in the past and no driver arrival/departure time has been entered for this stop'
            : undefined
      }
      className="w-full flex items-center gap-px"
    >
      <DateTimePicker
        key={pickerNonce}
        mode="date"
        inputVariant="compact"
        selectionType="single"
        autoApply
        openDirection="down"
        autoFlip
        labelType="none"
        placeholder="Date"
        usePortal
        className="flex-1 min-w-0 [&_div]:!min-w-0"
        inputClassName={
          apptFlagged
            ? '!border-red-500 !bg-red-100 dark:!border-red-400 dark:!bg-red-900/40'
            : undefined
        }
        disabled={isDisabled}
        value={parseMdYDate(sourceValue)}
        onChange={(v) => handleChange(v as Date | null)}
      />
      {/* Clear (X). On a PICKUP/DELIVERY scope the date cell is read-only, so
        * this is the only way to change the value from the grid — hence its own
        * permission and the driver-blank requirement inside canClearAppt.
        * Clearing reuses handleChange(null): formatMdYDate(null) is '', so the
        * delivery-boundary branch above fires the required reschedule modal with
        * initialNewDate null, and the cascade blanks the partner cell. */}
      <PermissionsContainer
        componentId="leg-appt-clear"
        componentName="Clear Appointment Date"
        defaultDisabled
        // Cog lives on the config-only anchor in the grid header, not here: this
        // cell vanishes on a complete leg, which would make the permission
        // ungrantable on a fully-run order.
        showHandle={false}
      >
        <button
          type="button"
          aria-label={clearAriaLabel ?? 'Clear appointment date'}
          title="Clear appointment date"
          disabled={!canClear || isPendingRemoved || !editing?.updateLegField}
          onClick={() => handleChange(null)}
          className="shrink-0 p-px rounded text-slate-400 hover:text-red-600 hover:bg-red-50 dark:text-gray-500 dark:hover:text-red-400 dark:hover:bg-red-900/30 disabled:opacity-30 disabled:hover:text-slate-400 disabled:hover:bg-transparent disabled:cursor-not-allowed"
        >
          <XMarkIcon className="h-3 w-3" />
        </button>
      </PermissionsContainer>
      {pendingChange && pronumber !== undefined && (
        <RescheduleEditModal
          pronumber={pronumber}
          billTo={billTo ?? undefined}
          required
          initialOldDate={pendingChange.oldIso}
          initialNewDate={pendingChange.newIso}
          downstreamApptClears={
            pendingChange.downstream
              ? {
                  cleared: pendingChange.downstream.clears.map((c) => c.legnum),
                  skipped: pendingChange.downstream.skips.map((sk) => ({
                    legnum: sk.legnum,
                    reason: sk.reason,
                  })),
                }
              : undefined
          }
          onClose={() => {
            // Cancel / X / backdrop — the appointment change is abandoned.
            // Nothing was staged (the leg edit only happens in onSaved), so
            // the order state is already correct; the picker just has to be
            // put back to what the cell actually holds. The save path lands
            // here too, after onSaved staged the new date.
            setPickerNonce((n) => n + 1);
            setPendingChange(null);
          }}
          onSaved={() => {
            // Apply the leg edit to pending state once the reschedule row
            // is persisted. The user can continue editing the order and
            // save the leg change as part of the broader order Save.
            editing?.updateLegField?.(legrecno, field, pendingChange.newMdY);
            // Downstream legs are staged alongside it, via the no-cascade
            // writer: updateLegField would push each cleared origApptDate back
            // onto the PREVIOUS leg's destApptDate, overwriting a leg the plan
            // deliberately left alone.
            const downstreamRecnos =
              pendingChange.downstream?.clears.map((c) => c.legrecno) ?? [];
            if (downstreamRecnos.length > 0) {
              editing?.clearLegApptDates?.(downstreamRecnos);
            }
            setPendingChange(null);
          }}
        />
      )}
    </div>
  );
};


const LegTimeCell: React.FC<DateTimeCellProps> = ({ field, value, disabled, ariaLabel }) => {
  const { editing, legrecno, isPendingRemoved } = React.useContext(LegEditingCtx);
  const isDisabled = !!disabled || isPendingRemoved || !editing?.updateLegField;
  const editedRaw = editing?.editedLegs?.[legrecno]
    ? (editing.editedLegs[legrecno] as Record<string, unknown>)[field]
    : undefined;
  const sourceValue: string =
    editedRaw !== undefined ? String(editedRaw ?? '') : String(value ?? '');
  return (
    <div role="group" aria-label={ariaLabel} className="w-full">
      <DateTimePicker
        mode="time"
        inputVariant="compact"
        selectionType="single"
        use24Hour
        openDirection="down"
        autoFlip
        labelType="none"
        placeholder="Time"
        usePortal
        className="w-full [&_div]:!min-w-0"
        disabled={isDisabled}
        value={parseHmTime(sourceValue)}
        onChange={(v) => editing?.updateLegField?.(legrecno, field, formatHmTime(v as Date | null))}
      />
    </div>
  );
};

interface LegTimeRangeCellProps {
  field1: string;
  value1?: string | null;
  field2: string;
  value2?: string | null;
  disabled?: boolean;
  ariaLabel?: string;
}

const LegTimeRangeCell: React.FC<LegTimeRangeCellProps> = ({ field1, value1, field2, value2, disabled, ariaLabel }) => {
  const { editing, legrecno, isPendingRemoved, isComplete } = React.useContext(LegEditingCtx);
  const { active: adminOverrideActive } = useAdminOverride();
  const isDisabled = !!disabled || isPendingRemoved || !editing?.updateLegField;

  const edited = editing?.editedLegs?.[legrecno] as Record<string, unknown> | undefined;
  const src1 = edited && field1 in edited ? String(edited[field1] ?? '') : String(value1 ?? '');
  const src2 = edited && field2 in edited ? String(edited[field2] ?? '') : String(value2 ?? '');

  // Completed legs are read-only — show the appt time(s) as bold text.
  // Released under Admin Override for the same reason as the date beside it.
  if (isComplete && !adminOverrideActive) {
    const display = [src1, src2].map((s) => s.trim()).filter(Boolean).join(' - ');
    return (
      <span
        aria-label={ariaLabel}
        className="inline-flex items-center h-5 text-[0.6875rem] font-bold text-slate-900 dark:text-gray-100 w-full"
      >
        {display || '—'}
      </span>
    );
  }

  const start = parseHmTime(src1);
  const end = parseHmTime(src2);
  const rangeValue: DateTimeValue | null = start || end
    ? { startDate: start ?? undefined, endDate: end ?? undefined }
    : null;

  const handleChange = (v: Date | DateTimeValue | null) => {
    if (!v) {
      editing?.updateLegField?.(legrecno, field1, '');
      editing?.updateLegField?.(legrecno, field2, '');
      return;
    }
    if (typeof v === 'object' && 'startDate' in v) {
      editing?.updateLegField?.(legrecno, field1, formatHmTime((v as DateTimeValue).startDate ?? null));
      editing?.updateLegField?.(legrecno, field2, formatHmTime((v as DateTimeValue).endDate ?? null));
    }
  };

  return (
    <div role="group" aria-label={ariaLabel} className="w-full">
      <DateTimePicker
        mode="time"
        selectionType="range"
        inputVariant="compact"
        use24Hour
        openDirection="down"
        autoFlip
        labelType="none"
        placeholder="Time"
        usePortal
        className="w-full [&_div]:!min-w-0"
        disabled={isDisabled}
        value={rangeValue}
        onChange={handleChange}
      />
    </div>
  );
};

const LegDateTimeCell: React.FC<DateTimeCellProps> = ({ field, value, disabled, ariaLabel }) => {
  const { editing, legrecno, isPendingRemoved } = React.useContext(LegEditingCtx);
  const isDisabled = !!disabled || isPendingRemoved || !editing?.updateLegField;
  const editedRaw = editing?.editedLegs?.[legrecno]
    ? (editing.editedLegs[legrecno] as Record<string, unknown>)[field]
    : undefined;
  const sourceValue: string =
    editedRaw !== undefined ? String(editedRaw ?? '') : String(value ?? '');
  return (
    <div role="group" aria-label={ariaLabel} className="w-full">
      <DateTimePicker
        mode="datetime"
        inputVariant="compact"
        selectionType="single"
        use24Hour
        openDirection="down"
        autoFlip
        labelType="none"
        placeholder="Date/Time"
        usePortal
        className="w-full [&_div]:!min-w-0"
        disabled={isDisabled}
        value={parseMdYHm(sourceValue)}
        onChange={(v) => editing?.updateLegField?.(legrecno, field, formatMdYHm(v as Date | null))}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Readonly address display. CityState comes from the location picker; users
// don't edit it directly — render plain text. Joins multi-line input with
// ", " to match the drawer's display (OrderDetailsModalContent.tsx ~3378).
// ---------------------------------------------------------------------------

const LegAddressDisplay: React.FC<{
  value?: string | null;
  ariaLabel?: string;
  recno?: number | string | null;
}> = ({ value, ariaLabel, recno }) => {
  const { getLocationInfo } = React.useContext(LocationInfoCtx);
  const info = getLocationInfo(recno);
  const hours = [info?.rcvhours1, info?.rcvhours2]
    .map((hour) => hour?.trim()).filter(Boolean).join(' – ');
  const text = value && value.trim() ? value.split('\n').join(', ') : '';
  return (
    <div className="min-w-0 w-full text-[0.6875rem]">
      <span
        aria-label={ariaLabel}
        className="text-blue-600 dark:text-blue-400 truncate block w-full"
      >
        {text || ' '}
      </span>
      {hours && (
        <span className="block text-slate-600 dark:text-gray-300">
          Receiving: {hours}
        </span>
      )}
      {/* Said in words as well as drawn as a ring: an outline alone is a colour nobody
          has been taught, and this one changes what the stop means. */}
      {isDeletedLocation(info) && (
        <span className="block font-semibold text-red-600 dark:text-red-400">
          Location deleted
        </span>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Origin / destination location cell. Wraps the shared LocationTypeAhead
// (live server search over /admin/locations?q=). Picking a location writes
// three fields in one shot: {side}code (recno), {side}Name, and
// {side}CityState. updateLegField's per-field cascade then mirrors each to
// the adjacent leg's matching field.
// ---------------------------------------------------------------------------

interface LegLocationCellProps {
  side: 'orig' | 'dest';
  value: number | string | null | undefined;
  /** Saved display name for `value` (leg.{orig,dest}Name) — resolves the
   *  input label without a lookup; an unsaved pick's name comes from the
   *  editedLegs overlay instead. */
  storedName?: string;
  disabled?: boolean;
  ariaLabel?: string;
  /** True when THIS cell's stop is CURRENTLY a delivery — for a dest cell that's
   *  effective legAction2 === 'DELIVERY', for an orig cell it's legtype ===
   *  'DELIVERY' (the two sides of the same physical delivery boundary). Drives
   *  the consignee-copy prompt on the on-screen delivery stop — not the order's
   *  original/saved one, which can differ after a rejection/re-delivery edit. */
  isDeliveryStop?: boolean;
}

const LegLocationCell: React.FC<LegLocationCellProps> = ({
  side,
  value,
  storedName,
  disabled,
  ariaLabel,
  isDeliveryStop,
}) => {
  const { editing, legrecno, isPendingRemoved } = React.useContext(LegEditingCtx);
  const { onDeliveryDestChange } = React.useContext(OrderContextCtx);
  const isDisabled = !!disabled || isPendingRemoved || !editing?.updateLegField;
  const codeField = side === 'orig' ? 'origcode' : 'destcode';
  const nameField = side === 'orig' ? 'origName' : 'destName';
  const cityStateField = side === 'orig' ? 'origCityState' : 'destCityState';

  // Effective recno (overlay > prop). A cleared edit writes the sentinel
  // value 0 (see clearDestSide in orderLegInheritance) — coerce 0 / null /
  // '' to empty string so TypeAhead resets its searchTerm. Without this,
  // mid-split would leave the stale label displayed even though the data
  // was cleared.
  const edited = editing?.editedLegs?.[legrecno] as Record<string, unknown> | undefined;
  const editedRaw = edited ? edited[codeField] : undefined;
  const effectiveCode = editedRaw !== undefined ? editedRaw : value;
  const sourceValue = effectiveCode ? String(effectiveCode) : '';
  const effectiveName =
    edited && edited[nameField] !== undefined ? String(edited[nameField] ?? '') : storedName;

  // Stale ring — same pattern as CI/CS.
  const stale = !!editing?.staleFields?.[legrecno]?.includes(codeField);

  // Retired-location ring. A leg can go on pointing at a location Data Management has
  // deleted — `/admin/locations/details` deliberately keeps serving it — and nothing on
  // screen said so, which is why prod has locations renamed "DO NOT USE …". Stale wins
  // the ring when both apply: that one is about the edit in front of you.
  const { getLocationInfo } = React.useContext(LocationInfoCtx);
  const retired = isDeletedLocation(getLocationInfo(effectiveCode as number | string | null));
  const clearStale = () => {
    if (stale) editing?.clearStaleField?.(legrecno, codeField);
  };

  const handlePick = (loc: LocationLite | null) => {
    clearStale();
    if (!editing?.updateLegField) return;
    const recno = loc ? loc.recno : 0;
    const name = loc ? locationDisplayName(loc) : '';
    // Three sequential calls compose via the hook's functional setter; each
    // call also fires its cascade to prev (orig) or next (dest), so the
    // adjacent leg's mirror fields stay in sync.
    editing.updateLegField(legrecno, codeField, recno);
    editing.updateLegField(legrecno, nameField, name);
    editing.updateLegField(legrecno, cityStateField, loc ? locationCityState(loc) : '');
    // Delivery stop's location changed → offer to copy it into the order/
    // template consignee (FIELD_AUDIT rule #4). One-directional. Fires for
    // EITHER side of the delivery boundary: a dest cell where legAction2 is
    // DELIVERY, or an orig cell where legtype is DELIVERY (the same physical
    // consignee stop). Keyed on the leg's CURRENT delivery status
    // (isDeliveryStop), so it prompts for the on-screen delivery, not the saved.
    if (recno && isDeliveryStop) {
      onDeliveryDestChange?.(recno, name);
    }
  };

  // "Add New Location" — the typeahead's pinned action opens the Locations
  // maintenance drawer in create mode, seeded with whatever was typed. The
  // saved location is then picked into this cell exactly like a dropdown pick
  // (same three-field write + cascade). If the drawer's address dedup finds an
  // existing (possibly deleted) location, it flips to edit/undelete that record
  // and the pick uses its recno instead — mirrors LocationMapField.
  const [addNewOpen, setAddNewOpen] = useState(false);
  const [addNewName, setAddNewName] = useState('');
  const [addNewMode, setAddNewMode] = useState<'create' | 'edit'>('create');
  const [addNewExisting, setAddNewExisting] = useState<LocationRecord | null>(null);

  const openAddNew = (term: string) => {
    setAddNewName(term.trim());
    setAddNewMode('create');
    setAddNewExisting(null);
    setAddNewOpen(true);
  };

  const handleAddNewSaved = (result?: LocationSaveResult) => {
    if (result?.id != null) {
      handlePick({
        recno: Number(result.id),
        lname: result.lname,
        cyalias: result.cyalias,
        ladd: result.ladd,
        lcity: result.lcity,
        lstate: result.lstate,
      });
    }
    setAddNewOpen(false);
  };

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-stale={stale ? 'true' : undefined}
      data-retired-location={retired ? 'true' : undefined}
      title={retired ? 'This location has been deleted in Data Management' : undefined}
      className={`w-full ${stale
        ? 'ring-2 ring-amber-400 bg-amber-50 dark:bg-amber-900/30 rounded'
        : retired
          ? 'ring-2 ring-red-500 bg-red-50 dark:bg-red-900/20 rounded'
          : ''}`}
    >
      <LocationTypeAhead
        value={sourceValue}
        valueLabel={effectiveName}
        onPick={handlePick}
        disabled={isDisabled}
        compact
        // #recno on dropdown rows (and, width permitting, the closed field) —
        // disambiguates an id search from street-number address matches.
        withRecnoSuffix
        className="w-full"
        onAddNew={isDisabled ? undefined : openAddNew}
      />
      {addNewOpen && (
        <Suspense fallback={null}>
          <LocationEditDrawer
            isOpen
            mode={addNewMode}
            location={addNewExisting}
            initialName={addNewName}
            onClose={() => setAddNewOpen(false)}
            onSaved={handleAddNewSaved}
            onEditExisting={(dupe: DupeLocation) => {
              setAddNewMode('edit');
              setAddNewExisting(dupe as unknown as LocationRecord);
            }}
          />
        </Suspense>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Driver cell wrapper — renders the real DriverSelectorCell on the last leg of
// a dispatch scope (mirroring the Order Details drawer behavior). Earlier legs
// in the same scope and read-only layouts (no `pronumber`) render nothing,
// matching the drawer's "driver lives on the terminating leg" rule.
// ---------------------------------------------------------------------------

interface LegDriverCellProps {
  leg: ProLeg;
  /** When true, render DriverSelectorCell + Send-to-Move side-by-side for the
   * wide layout's bottom strip. Default `false` keeps the original stacked
   * column-style layout used by the cards header. */
  horizontal?: boolean;
  /** Whether this instance shows the permissions cog. The driver field renders
   * once per leg but they all share the `leg-driver-field` componentId, so only
   * the first leg surfaces the cog to avoid a stack of duplicates. */
  showCog?: boolean;
}

// Read-only driver display for non-last legs in a multi-leg dispatch scope.
// Three states (in priority order):
//   1. Mismatch  → red chip + warning icon + "Mismatched Driver on Linked
//                  Move" tooltip. Same case the original LegMismatchWarning
//                  handled.
//   2. Complete  → neutral slate chip surfacing the driver who ran the leg
//                  (see isLegComplete). Useful historical info on linked-move
//                  chains where earlier legs are done.
//   3. Otherwise → render nothing (incomplete leg, no conflict).
const LegLinkedDriverDisplay: React.FC<{ leg: ProLeg }> = ({ leg }) => {
  const { allLegs, pronumber } = React.useContext(OrderContextCtx);
  const driver = (leg.driver ?? '').trim();
  const mismatched = hasDriverMismatch(leg, allLegs);
  const isComplete = isLegComplete(leg);

  // Safety System + moves icons only make sense for a real GWI unit. Outside
  // carriers — the '000' code or any code containing a letter, same rule
  // isLegComplete uses — have no driver safety record and no moves to list.
  // The icons live OUTSIDE the mismatch Tooltip so its cursor-help overlay
  // doesn't swallow the clicks.
  const iconLinks =
    driver !== '' && driver !== '000' && !/[a-z]/i.test(driver) ? (
      <DriverIconLinks
        unitNumber={driver}
        highlightPronumber={pronumber}
        showMoves={legIsInTodaysMoves({
          origApptDate: leg.origApptDate,
          destApptDate: leg.destApptDate,
          departDateTime: leg.destDepartDateTime,
        })}
      />
    ) : null;

  if (mismatched) {
    return (
      <div className="flex items-center gap-1">
        <Tooltip content="Mismatched Driver on Linked Move" placement="top">
          <div
            className="flex items-center gap-1 cursor-help"
            data-testid={`leg-driver-mismatch-${leg.legnum}`}
          >
            <Icon.warn className="w-4 h-4 text-red-500 dark:text-red-400 flex-shrink-0" aria-hidden="true" />
            <span className="text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-800 rounded px-1.5 py-0.5">
              Driver: {driver}<MuleBadge isMule={leg.isMule} />
            </span>
          </div>
        </Tooltip>
        {iconLinks}
      </div>
    );
  }
  if (isComplete && driver) {
    return (
      <div
        className="flex items-center gap-1"
        data-testid={`leg-driver-linked-${leg.legnum}`}
      >
        <span className="text-xs font-medium text-slate-700 dark:text-gray-300 bg-slate-100 dark:bg-gray-800 border border-slate-300 dark:border-gray-600 rounded px-1.5 py-0.5">
          Driver: {driver}<MuleBadge isMule={leg.isMule} />
        </span>
        {iconLinks}
      </div>
    );
  }
  return null;
};

/**
 * Send-to-GWI-Move toggle. Renders only on the last leg in scope. The drawer
 * differentiates "current" (post-overlay) from "original" (raw server value)
 * to gate the disable + onChange logic. `leg` here comes from the iterator
 * over the rendered `legs` prop — which is the raw, un-overlaid list — so the
 * leg arg itself is the "original" and per-field current values come from
 * `editedLegs`. When editing isn't wired, the onChange is a no-op.
 */
const LegSendToMoveToggle: React.FC<{ leg: ProLeg }> = ({ leg }) => {
  const { allLegs, isTemplate } = React.useContext(OrderContextCtx);
  const { editing } = React.useContext(LegEditingCtx);
  const { driverAccess, effectiveDispatcherIsGwi } = useOrderFormContext();

  // Only render on last leg in scope.
  if (!isLastInScope(leg, allLegs)) return null;
  // Paid legs already shipped to gwiMove (or won't ever) — the toggle is
  // meaningless once payroll is calculated, so hide it entirely.
  if (leg.isPaid) return null;

  const editedLeg = editing?.editedLegs?.[leg.legrecno] as
    | Record<string, unknown>
    | undefined;
  const editedDriverRaw =
    editedLeg && 'driver' in editedLeg ? editedLeg.driver : undefined;
  const currentDriver =
    editedDriverRaw !== undefined
      ? String(editedDriverRaw ?? '').trim()
      : (leg.driver ?? '').trim();
  // `leg` is the EFFECTIVE leg (post-edit overlay), so its driver/
  // sendToGwiMove already reflect the user's in-flight edits. The disable
  // rule below needs the SERVER snapshot — look up the matching raw leg
  // from `originalLegs`. Pending-new legs have no server state so default
  // both originals to falsy (toggle is freely changeable).
  const originalLeg = editing?.originalLegs?.find((l) => l.legrecno === leg.legrecno);
  const originalDriver = (originalLeg?.driver ?? '').trim();
  const originalSendToGwiMove = !!originalLeg?.sendToGwiMove;
  const currentSendToGwiMove =
    editedLeg && 'sendToGwiMove' in editedLeg
      ? !!editedLeg.sendToGwiMove
      : !!leg.sendToGwiMove;

  const handleChange = (checked: boolean) => {
    if (!editing?.updateLegField) return;
    if (checked && currentDriver) {
      editing.updateLegField(leg.legrecno, 'sendToGwiMove', true);
      return;
    }
    if (!checked && (!originalSendToGwiMove || currentDriver !== originalDriver)) {
      editing.updateLegField(leg.legrecno, 'sendToGwiMove', false);
    }
  };

  // Apply the same shared canEditDriver gate the driver cell uses — toggling
  // Send-to-Move without a valid driver edit is meaningless, so the toggle
  // locks alongside the driver cell.
  const scope = getDispatchScope(leg.legrecno, allLegs);
  const scopeLegs = scope
    .map((legrecnoInScope) => allLegs.find((l) => l.legrecno === legrecnoInScope))
    .filter((l): l is ProLeg => !!l);
  const canDriver = !isTemplate && canEditDriver({
    driverAccess,
    effectiveDispatcherIsGwi,
    scopeLegs,
    edits: editing?.editedLegs ?? {},
  });

  // Disable when driver editing is locked, OR when the user is trying to
  // *uncheck* a leg that was originally sent and whose driver hasn't changed
  // — protects against accidental un-toggles on already-dispatched moves.
  const sendToGwiMoveDisabled =
    !canDriver ||
    (currentSendToGwiMove && originalSendToGwiMove && currentDriver === originalDriver);

  return (
    <div
      className="flex items-center justify-end gap-1.5"
      data-testid={`leg-send-to-move-row-${leg.legnum}`}
    >
      <span className="text-[0.59375rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">
        Send to Move
      </span>
      <Toggle
        size="sm"
        checked={currentSendToGwiMove}
        onChange={handleChange}
        disabled={sendToGwiMoveDisabled}
        ariaLabel={`Leg ${leg.legnum} send to move`}
      />
    </div>
  );
};

const LegDriverCell: React.FC<LegDriverCellProps> = ({ leg, horizontal = false, showCog = false }) => {
  const { editing } = React.useContext(LegEditingCtx);
  const order = React.useContext(OrderContextCtx);
  const { pronumber, allLegs } = order;
  const { requestClearDriver } = React.useContext(ClearDriverCtx);
  const { driverAccess, effectiveDispatcherIsGwi } = useOrderFormContext();

  if (!isLastInScope(leg, allLegs)) {
    // Non-last legs in scope don't get an editable driver cell — the last
    // leg owns the dispatch driver. Surface a read-only chip when the leg
    // is complete (so historical info doesn't disappear on linked moves)
    // and a red chip when the assigned driver mismatches the last-leg
    // driver; otherwise render nothing.
    return <LegLinkedDriverDisplay leg={leg} />;
  }
  if (pronumber === undefined) return null;

  // Prefer the edited driver value (mirrors how CS resolves its own state).
  const editedRaw = editing?.editedLegs?.[leg.legrecno]
    ? (editing.editedLegs[leg.legrecno] as Record<string, unknown>).driver
    : undefined;
  const driverValue =
    editedRaw !== undefined
      ? String(editedRaw ?? '')
      : (leg.driver ?? '');

  // Resolve the dispatch scope's leg list and apply the shared canEditDriver
  // gate (drawer parity: non-GWI dispatcher, paid scope leg, or scope-locked
  // timestamps disable driver editing).
  const scope = getDispatchScope(leg.legrecno, allLegs);
  const scopeLegs = scope
    .map((legrecnoInScope) => allLegs.find((l) => l.legrecno === legrecnoInScope))
    .filter((l): l is ProLeg => !!l);
  // Outside-carrier moves record the carrier's SCAC as a pseudo-driver instead
  // of dispatching a GWI unit, so the non-GWI-dispatcher lock in canEditDriver
  // doesn't apply (allowNonGwi). Every OTHER lock still holds — in particular a
  // paid or completed leg stays locked, so a GWI driver who's already been paid
  // for a leg is never silently replaced by the outside carrier. Only ever true
  // for real orders: ProDetail passes an empty outsideCarrierDriver for
  // templates, whose driver fields never open.
  const isOutsideCarrierMove = !!(order.outsideCarrierDriver && order.outsideCarrierDriver.trim());
  // Templates never dispatch — the driver field stays locked even if a GWI
  // dispatcher is set (dispatcher is editable on templates).
  const canDriver = !order.isTemplate && canEditDriver({
    driverAccess,
    effectiveDispatcherIsGwi,
    scopeLegs,
    edits: editing?.editedLegs ?? {},
    allowNonGwi: isOutsideCarrierMove,
  });

  // Auto-fill missing appt dates on driver assignment, mirroring the drawer's
  // autoFillDatesIfNeeded (OrderDetailsModalContent.tsx ~2060). Logic lives in
  // utils/scopeDateAutofill so save (useOrderEditing.save) can run the same
  // pass as a safety net for post-assignment leg edits that opened new holes.
  const maybeAutoFillDates = () => {
    if (!editing?.updateLegField) return;
    const fills = computeScopeApptFills(leg.legrecno, allLegs);
    if (!fills) return;
    for (const [legrecnoStr, fill] of Object.entries(fills)) {
      const lr = Number(legrecnoStr);
      if (fill.origApptDate) editing.updateLegField!(lr, 'origApptDate', fill.origApptDate);
      if (fill.destApptDate) editing.updateLegField!(lr, 'destApptDate', fill.destApptDate);
    }
  };

  // "000 driver" quick-add button (permission `leg-000-driver`, defaultHidden).
  // Lets a group (e.g. LCs) stamp the outside-carrier "000" unit WITHOUT holding
  // `leg-driver-field` edit access — so it must live OUTSIDE that container (a
  // hidden leg-driver-field would otherwise take it down with the cell). It only
  // ever writes "000" — never any other unit — and only surfaces when:
  //   - not a template (templates never dispatch), AND
  //   - the leg has no driver yet (never clobbers a real assignment), AND
  //   - no leg in the dispatch scope is paid (payroll safety — mirrors canEditDriver).
  const scopeHasPaidLeg = scopeLegs.some((l) => l.isPaid === true);
  const canShow000Button =
    !order.isTemplate &&
    !!editing?.updateLegField &&
    driverValue.trim() === '' &&
    !scopeHasPaidLeg;
  // The four driver actuals. Shared by the 000 add (stamp) and clear (blank)
  // paths and by the cell-X confirmation check below.
  const driverDateFields = [
    'origArrivalDateTime',
    'origDepartDateTime',
    'destArrivalDateTime',
    'destDepartDateTime',
  ] as const;
  // 000 means an outside carrier already ran the move, so there are no real
  // driver actuals to wait on: stamp all four date/times on every leg in the
  // dispatch scope (the driver write cascades across the same scope) so the
  // leg reads complete the moment 000 goes on. The stamp is the PRO's date for
  // that end of the leg — origin pair from the origin appt date, destination
  // pair from the destination appt date, each with the current clock time of
  // day (EDI wants an actual time, never 00:00 — Lee, 2026-10-29). A leg with
  // no appt date to copy gets now; stamping today's DATE on a move that
  // outgated days earlier put the wrong day on the leg (PRO 5071414, 2026-10-27).
  const handleAdd000Driver = () => {
    if (!editing?.updateLegField) return;
    editing.updateLegField(leg.legrecno, 'driver', '000');
    const now = new Date();
    for (const scopeLeg of scopeLegs) {
      const origStamp = outsideCarrierDateStamp(scopeLeg.origApptDate, now);
      const destStamp = outsideCarrierDateStamp(scopeLeg.destApptDate, now);
      editing.updateLegField(scopeLeg.legrecno, 'origArrivalDateTime', origStamp);
      editing.updateLegField(scopeLeg.legrecno, 'origDepartDateTime', origStamp);
      editing.updateLegField(scopeLeg.legrecno, 'destArrivalDateTime', destStamp);
      editing.updateLegField(scopeLeg.legrecno, 'destDepartDateTime', destStamp);
    }
    maybeAutoFillDates();
  };
  // Clear 000 is the exact undo of the add: the button stamped those four
  // date/times itself, so it takes them back too — no cell-X confirmation.
  const handleClear000Driver = () => {
    if (editing?.clearDriver) {
      editing.clearDriver(leg.legrecno, { clearDates: true });
      return;
    }
    if (!editing?.updateLegField) return;
    editing.updateLegField(leg.legrecno, 'driver', '');
    for (const scopeLeg of scopeLegs) {
      for (const f of driverDateFields) editing.updateLegField(scopeLeg.legrecno, f, '');
    }
  };

  // Undo side of the same button, on the same permission: once 000 is saved the
  // LC has no other way off it (the driver CELL is locked/hidden for them), and
  // a leg with a driver can no longer have its appointment date cleared —
  // canClearAppt requires every driver in the scope to be blank. Clearing 000
  // hands both back and blanks the four date/times the add stamped. Same
  // conditions as the add: real order, unpaid scope.
  const canShowClear000Button =
    !order.isTemplate &&
    !!editing?.updateLegField &&
    driverValue.trim() === '000' &&
    !scopeHasPaidLeg;

  // Clear path for the cell's X — mirrors the drawer's clearDriverAssignment
  // flow (Clear 000 has its own, unprompted path above). When ANY leg in the dispatch scope has
  // a recorded arrival/depart datetime, route through the confirmation modal so
  // the user can decide whether to drop those timestamps too. Otherwise commit
  // the clear via the hook's clearDriver (cascades across scope automatically).
  // `revert` undoes the caller's optimistic UI when the user backs out: the cell
  // clears its own state eagerly, the button has nothing to put back.
  const attemptClearDriver = (revert: () => void = () => {}) => {
    const anyDates = scopeLegs.some((scopeLeg) =>
      driverDateFields.some((f) => !!(scopeLeg as unknown as Record<string, unknown>)[f]),
    );
    if (anyDates) {
      requestClearDriver(leg.legrecno, revert);
      return;
    }
    if (editing?.clearDriver) {
      editing.clearDriver(leg.legrecno, { clearDates: false });
    } else {
      // Fallback for callers wired only with updateLegField — the hook's
      // updateLegField already cascades driver writes across the scope.
      editing?.updateLegField?.(leg.legrecno, 'driver', '');
    }
  };

  return (
    <div
      className={
        horizontal
          ? 'flex flex-row items-center gap-2'
          : 'flex flex-col items-stretch gap-1.5'
      }
    >
    <PermissionsContainer componentId="leg-driver-field" componentName="Driver Field" defaultDisabled showCog={showCog}>
    <div
      className={
        horizontal
          ? 'flex flex-row items-center gap-2'
          : 'flex flex-col items-stretch gap-1.5'
      }
    >
      {horizontal && <LegSendToMoveToggle leg={leg} />}
      <div>
      <DriverSelectorCell
        pronumber={pronumber}
        legnum={leg.legnum}
        driver={driverValue}
        originAppointmentDate={leg.origApptDate ?? undefined}
        destinationAppointmentDate={leg.destApptDate ?? undefined}
        legDepartDateTime={leg.destDepartDateTime}
        originCoordinates={leg.origcoordinates ?? undefined}
        destinationCoordinates={leg.destcoordinates ?? undefined}
        originName={leg.origName ?? undefined}
        destinationName={leg.destName ?? undefined}
        customerName={order.customerName}
        hazmat={order.hazmat}
        isDaycab={order.isDaycab}
        isTank={order.isTank}
        overweightPermitRequired={order.overweightPermitRequired}
        extraDriverOption={order.outsideCarrierDriver}
        allowMissingDates
        dispatchBlockedReason={blockingTroubleTicketMessage(order.openTickets, leg.legAction2, legAtOrBeforeRejectedGroup(allLegs, leg.legnum))}
        dispatchWarnings={(() => {
          // Mirrors drawer at OrderDetailsModalContent.tsx ~3990-4017.
          // Resolve scope legs with edit overlay so unsaved legtype/destApptDate
          // edits flow into the warning checks.
          const scopeLegs = scope
            .map((rn) => allLegs.find((l) => l.legrecno === rn))
            .filter((l): l is ProLeg => !!l);
          const warnings: string[] = [];
          const expired = redeliveryExpirationWarning(order, scopeLegs);
          if (expired) warnings.push(expired);
          if (
            needsDropReleaseWarning({
              isDrop: order.isDrop,
              // On-screen delivery legnum, not the saved one: an inserted or
              // removed stop ahead of the delivery renumbers the legs on
              // screen while order.deliveryLeg still holds the saved number.
              deliveryLeg: resolveDeliveryLegnum(allLegs, order.deliveryLeg),
              dropReleaseDatetime: order.dropReleaseDatetime,
              dispatchGroupLegnums: scopeLegs.map((l) => l.legnum),
            })
          ) {
            warnings.push(DROP_RELEASE_WARNING_MESSAGE);
          }
          if (
            needsNotReceivingWarning({
              moveType: order.moveType,
              firstRcvDate: order.firstRcv,
              dispatchGroupLegs: scopeLegs.map((l) => ({
                legtype: l.legtype ?? undefined,
                destApptDate: l.destApptDate ?? undefined,
              })),
            })
          ) {
            warnings.push(NOT_RECEIVING_WARNING_MESSAGE);
          }
          if (needsNotRailbilledWarning({
            warnings: order.warnings,
            dispatchGroupLegs: scopeLegs.map((l) => ({
              legtype: l.legtype ?? undefined,
            })),
            allOrderLegs: allLegs.map((l) => ({
              legtype: l.legtype ?? undefined,
            })),
          })) {
            warnings.push(NOT_RAILBILLED_WARNING_MESSAGE);
          }
          if (needsMissingPreadviseWarning({
            warnings: order.warnings,
            dispatchGroupLegs: scopeLegs.map((l) => ({
              legtype: l.legtype ?? undefined,
            })),
            allOrderLegs: allLegs.map((l) => ({
              legtype: l.legtype ?? undefined,
            })),
          })) {
            warnings.push(MISSING_PREADVISE_WARNING_MESSAGE);
          }
          // A trouble ticket belongs to the whole order, so this one gets no
          // scopeLegs treatment — see openTroubleTicketCheck.
          const openTt = openTroubleTicketWarning(order.openTtCount);
          if (openTt) {
            warnings.push(openTt);
          }
          // Same for a repair order: it is filed against the container or the
          // chassis the whole move rides — see openRepairOrderCheck.
          const openRo = openRepairOrderWarning(order.openRoCount);
          if (openRo) {
            warnings.push(openRo);
          }
          return warnings;
        })()}
        onDriverAssigned={(_pn, _legnum, value) => {
          editing?.updateLegField?.(leg.legrecno, 'driver', value);
          if (value) maybeAutoFillDates();
        }}
        onClearAttempt={(_pn, _legnum, revert) => attemptClearDriver(revert)}
        getLegContext={() => buildLegContextForValidation(allLegs)}
        getOrderOverrides={() => buildOrderOverridesForValidation(editing?.editedOrderFields)}
        revalidationKey={order.driverRevalidationKey}
        onValidationChange={(_pn, lg, isValid, missing) =>
          order.recordLegValidation?.(lg, isValid, missing)
        }
        overrideApproval={(() => {
          const trimmed = driverValue.trim();
          if (!trimmed) return undefined;
          // Scope-wide lookup: after a drop-flag change combines legs, an
          // approval recorded on a sibling leg in the same group still
          // covers the driver here. Walk the scope's legrecnos and surface
          // the first matching approval. The originating leg's CURRENT
          // legnum (mapped from allLegs) goes into the tooltip when it's a
          // different leg than this one.
          for (const rn of scope) {
            const a = order.overrideApprovalByKey?.get(`${rn}:${trimmed}`);
            if (!a) continue;
            const sourceLeg = allLegs.find((l) => l.legrecno === a.legrecno);
            return {
              username: a.username,
              created_at: a.created_at,
              reason: a.reason,
              legnum: sourceLeg?.legnum ?? a.legnum,
            };
          }
          return undefined;
        })()}
        disabled={!editing?.updateLegField || !canDriver}
      />
      </div>
      {!horizontal && <LegSendToMoveToggle leg={leg} />}
    </div>
    </PermissionsContainer>
    {/* Sibling of the leg-driver-field container (not nested) so it stays
        visible for groups granted `leg-000-driver` even when their driver cell
        is locked/hidden. defaultHidden → nobody sees it until an admin grants a
        group; self-registers via the container (no migration). Add and clear
        share the one permission — a group that can stamp 000 can take it back
        off, and the two are mutually exclusive on a given leg. */}
    {(canShow000Button || canShowClear000Button) && (
      <PermissionsContainer
        componentId="leg-000-driver"
        componentName="000 Driver Button"
        defaultHidden
      >
        {canShow000Button ? (
          <button
            type="button"
            onClick={handleAdd000Driver}
            className="self-start px-2 py-0.5 text-xs bg-slate-600 hover:bg-slate-700 text-white rounded transition-colors whitespace-nowrap"
            title="Assign outside-carrier unit 000 (no GWI driver)"
            data-testid={`add-000-driver-${leg.legnum}`}
          >
            000
          </button>
        ) : (
          <button
            type="button"
            onClick={handleClear000Driver}
            className="self-start px-2 py-0.5 text-xs bg-slate-600 hover:bg-red-700 text-white rounded transition-colors whitespace-nowrap"
            title="Remove outside-carrier unit 000 from this move"
            data-testid={`clear-000-driver-${leg.legnum}`}
          >
            Clear 000
          </button>
        )}
      </PermissionsContainer>
    )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// LegBottomBar — the single horizontal strip that hosts Status, the travel-
// history icon, the dispatch-info icon, and the driver group (Send-to-Move
// toggle + DriverSelectorCell). Used by both the wide layout's row 4 and the
// cards layout's bottom strip. All content is left-aligned.
// ---------------------------------------------------------------------------

interface LegBottomBarProps {
  leg: ProLeg;
  scope: ScopeMembership;
  /** Forwarded to LegDriverCell so only the first leg's driver field shows the
   * shared `leg-driver-field` permissions cog. */
  isFirstLeg?: boolean;
}

const LegBottomBar: React.FC<LegBottomBarProps> = ({ leg, scope, isFirstLeg = false }) => (
  <div className="flex items-center gap-2 w-full min-w-0">
    <LegStateBadges leg={leg} scope={scope} />
    <LegTravelHistoryButton leg={leg} />
    <LegDispatchInfoButton leg={leg} showCog={isFirstLeg} />
    <LegSendDispatchButton leg={leg} />
    <div className="ml-auto">
      <LegDriverCell leg={leg} horizontal showCog={isFirstLeg} />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Wide-layout header
// ---------------------------------------------------------------------------

interface HeaderLabelProps {
  children?: React.ReactNode;
  fields?: ReadonlyArray<string>;
  variant?: 'default' | 'orig' | 'dest';
  className?: string;
  style?: React.CSSProperties;
}

const HeaderLabel: React.FC<HeaderLabelProps> = ({
  children,
  fields,
  variant = 'default',
  className,
  style,
}) => {
  const { hField } = React.useContext(HoverCtx);
  const hit = !!fields && hField != null && fields.includes(hField);

  const palette = {
    default: { base: 'bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-gray-300', hit: 'bg-slate-200 dark:bg-gray-700' },
    orig: { base: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300', hit: 'bg-blue-100 dark:bg-blue-900/50' },
    dest: { base: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300', hit: 'bg-amber-200 dark:bg-amber-900/60' },
  }[variant];

  return (
    <div
      style={style}
      className={
        'px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wider ' +
        'border-b border-slate-300 dark:border-gray-600 border-r border-slate-200 dark:border-gray-700 ' +
        'whitespace-nowrap overflow-hidden text-ellipsis ' +
        'transition-colors duration-100 ' +
        (hit ? palette.hit : palette.base) +
        (className ? ` ${className}` : '')
      }
    >
      {children}
    </div>
  );
};

interface DataCellProps {
  children?: React.ReactNode;
  field?: string;
  className?: string;
  style?: React.CSSProperties;
}

const DataCell: React.FC<DataCellProps> = ({ children, field, className, style }) => {
  const { hField, setHField } = React.useContext(HoverCtx);
  const hit = field != null && hField === field;
  return (
    <div
      onMouseEnter={field != null ? () => setHField(field) : undefined}
      onMouseLeave={field != null ? () => setHField(null) : undefined}
      style={style}
      className={
        'px-1 py-0.5 border-r border-slate-100 dark:border-gray-700 border-b border-slate-100 dark:border-gray-700 ' +
        'flex items-center gap-1 min-w-0 transition-colors duration-100 ' +
        (hit ? 'bg-slate-50 dark:bg-gray-800' : '') +
        (className ? ` ${className}` : '')
      }
    >
      {children}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Driver-ETA cells — a clock time (24h) colored by projected status. Sourced
// from the driver-status-monitor projection (etaByLeg, keyed by legnum) and
// surfaced through OrderContextCtx. Each leg carries TWO projections — arrival
// at its origin location and at its destination location — so the ETA renders
// once per location rather than once per leg. Blank when there's no driver /
// the leg is completed / the projection hasn't loaded, and blank on the origin
// side once the driver is already there (the move went active).
// ---------------------------------------------------------------------------

const ETA_STATUS_CLASS: Record<ProjectedStatus, string> = {
  on_track: 'text-emerald-700 dark:text-emerald-400',
  at_risk: 'text-amber-600 dark:text-amber-400',
  will_miss: 'text-red-600 dark:text-red-400',
  missed: 'text-red-700 line-through dark:text-red-400',
};

type EtaSide = 'orig' | 'dest';

const ETA_SIDE_TITLE: Record<EtaSide, string> = {
  orig: 'Projected arrival at origin',
  dest: 'Projected arrival at destination',
};

/** The projected arrival for one side of a leg, or null when that side has no
 *  meaningful projection (origin already reached, driver not started, …). */
function etaForSide(eta: LegEta, side: EtaSide): Date | null {
  return side === 'orig' ? eta.etaToOrigin : eta.etaToDestination;
}

/** "Not started" is a DRIVER-level state, not per-location — surface it once,
 *  on the origin side, so a leg doesn't repeat it twice. */
function showsNotStarted(eta: LegEta, side: EtaSide): boolean {
  return eta.notStarted && side === 'orig';
}

/** Labeled variant — used by the cards layout, where a LegField supplies the
 *  "ETA" caption. */
function LegEtaCell({ eta, side }: { eta?: LegEta; side: EtaSide }) {
  if (!eta) return null; // no driver / completed / not yet loaded → blank
  if (eta.notStarted) {
    return showsNotStarted(eta, side) ? (
      <span className="text-[0.6875rem] text-slate-400 dark:text-gray-500">Not started</span>
    ) : null;
  }
  const value = etaForSide(eta, side);
  if (!value) return null;
  return (
    <span
      className={`text-[0.6875rem] font-semibold tabular-nums inline-flex items-center ${ETA_STATUS_CLASS[eta.status]}`}
      title={ETA_SIDE_TITLE[side]}
    >
      {formatETA(value)}
    </span>
  );
}

/** Inline variant — a compact right-aligned chip that rides in the wide grid's
 *  origin/destination NAME cell, so the time sits with the place it belongs to.
 *  Carries its own "ETA" caption since the grid has no ETA column header.
 *
 *  TIMES ONLY. The wider "Not started" wording would squeeze the location
 *  typeahead into truncating its value, so that state is rendered once per leg
 *  in the details stack instead (LegEtaNotStartedRow) where space is free. */
function LegEtaChip({
  eta,
  side,
  legnum,
}: {
  eta?: LegEta;
  side: EtaSide;
  legnum: number;
}) {
  const value = eta && !eta.notStarted ? etaForSide(eta, side) : null;
  if (!eta || !value) return null;
  return (
    <span
      className={
        'ml-auto shrink-0 inline-flex items-center gap-1 rounded px-1 py-px ' +
        'text-[0.625rem] font-semibold tabular-nums normal-case ' +
        'border border-slate-200 dark:border-gray-600 bg-white/70 dark:bg-gray-900/40 ' +
        ETA_STATUS_CLASS[eta.status]
      }
      title={ETA_SIDE_TITLE[side]}
      aria-label={`Leg ${legnum} ${side === 'orig' ? 'origin' : 'destination'} ETA`}
      data-testid={`leg-eta-${side}-${legnum}`}
    >
      <span className="text-[0.5625rem] font-bold uppercase tracking-wider text-slate-400 dark:text-gray-500">
        ETA
      </span>
      {formatETA(value)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Per-leg estimated driver pay + mile-allocated margin, from the V1 showmargin
// read-model (marginByLeg, keyed by legnum). Both render blank when the leg has
// no driver-pay row (no driver / not yet loaded).
// ---------------------------------------------------------------------------

function fmtUSD(n: number): string {
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Tooltip text for a leg's pay cell. A paid leg (settled dp_li) is an ACTUAL,
 * not an estimate, so it drops the "Estimated" wording and names the payroll
 * date. MISSING RATE / DO NOT PAY states take precedence.
 */
export function legPayTooltip(pay: DriverPayLeg): string {
  if (pay.state !== 'OK') return pay.state;
  if (pay.isPaid) {
    // payrollDate is the pay-week ENDING date, not the payment date (actual
    // payment is ~12 days later), so label it "WE" rather than "paid".
    return pay.payrollDate
      ? `Driver pay — WE ${pay.payrollDate}`
      : 'Driver pay (settled)';
  }
  return 'Estimated driver pay for this leg';
}

function LegPayCell({ pay, loading }: { pay?: DriverPayLeg; loading?: boolean }) {
  if (!pay) {
    // Margin/pay is fetched after the drawer opens — show a placeholder while
    // it loads instead of a blank cell that reads as "no pay".
    return loading ? (
      <span className="text-[0.6875rem] italic text-slate-400 dark:text-gray-500 inline-flex items-center">
        Loading…
      </span>
    ) : null;
  }
  const warn = pay.state !== 'OK';
  return (
    <Tooltip content={legPayTooltip(pay)} placement="top">
      <span
        className={`text-[0.6875rem] font-semibold inline-flex items-center ${
          warn
            ? 'text-amber-600 dark:text-amber-400'
            : 'text-slate-700 dark:text-gray-300'
        }`}
      >
        {fmtUSD(pay.legPay)}
      </span>
    </Tooltip>
  );
}

function LegMarginCell({
  pay,
  marginLimit,
}: {
  pay?: DriverPayLeg;
  marginLimit?: number;
}) {
  if (!pay || pay.marginPct == null) return null;
  const under = marginLimit != null && isBelowMarginLimit(pay.marginPct, marginLimit);
  return (
    <Tooltip
      content={
        under
          ? `Below the ${marginLimit}% margin limit`
          : 'Leg margin (mile-allocated)'
      }
      placement="top"
    >
      <span
        className={`text-[0.6875rem] font-semibold inline-flex items-center ${
          under
            ? 'text-red-600 dark:text-red-400'
            : 'text-emerald-700 dark:text-emerald-400'
        }`}
      >
        {pay.marginPct.toFixed(1)}%
      </span>
    </Tooltip>
  );
}

const LegsHeader: React.FC = () => (
  <div className="grid" style={{ gridTemplateColumns: GRID_TEMPLATE }}>
    {/* # column spans all three header rows */}
    <HeaderLabel style={{ gridRow: '1 / span 3' }} className="border-r-slate-300 dark:border-r-gray-600" />

    {/* Row 1 */}
    <HeaderLabel variant="orig" fields={['ot']}>Move Type</HeaderLabel>
    <HeaderLabel variant="orig" fields={['oa']}>Appt #</HeaderLabel>
    <HeaderLabel variant="orig" fields={['od']}>Appt Date</HeaderLabel>
    <HeaderLabel variant="orig" fields={['otm']}>Time</HeaderLabel>
    {/* Splitter column header — spans all three rows, blank */}
    <HeaderLabel style={{ gridColumn: 6, gridRow: '1 / span 3' }} className="!p-0" />
    <HeaderLabel variant="dest" fields={['dt']}>Move Type</HeaderLabel>
    <HeaderLabel variant="dest" fields={['da']}>Appt #</HeaderLabel>
    <HeaderLabel variant="dest" fields={['dd']}>Appt Date</HeaderLabel>
    <HeaderLabel variant="dest" fields={['dtm']}>Time</HeaderLabel>
    <HeaderLabel
      fields={['det']}
      style={{ gridColumn: 11, gridRow: '1 / span 3' }}
      className="flex items-center justify-center"
    >
      Details
    </HeaderLabel>
    {/* Trailing actions column (×) — no header label */}
    <HeaderLabel style={{ gridColumn: 12, gridRow: '1 / span 3' }} />

    {/* Row 2 */}
    <HeaderLabel variant="orig" fields={['on']} style={{ gridColumn: '2 / span 2' }}>
      Origin
    </HeaderLabel>
    <HeaderLabel variant="orig" fields={['oar']}>Orig Arrival</HeaderLabel>
    <HeaderLabel variant="orig" fields={['ode']}>Orig Departure</HeaderLabel>
    <HeaderLabel variant="dest" fields={['dn']} style={{ gridColumn: '7 / span 2' }}>
      Destination
    </HeaderLabel>
    <HeaderLabel variant="dest" fields={['dar']}>Dest Arrival</HeaderLabel>
    <HeaderLabel variant="dest" fields={['dde']}>Dest Departure</HeaderLabel>

    {/* Row 3 */}
    <HeaderLabel variant="orig" fields={['oad']} style={{ gridColumn: '2 / span 2' }}>
      Address
    </HeaderLabel>
    <HeaderLabel variant="orig" fields={['om']} style={{ gridColumn: '4 / span 2' }}>
      Orig Memo
    </HeaderLabel>
    <HeaderLabel variant="dest" fields={['dad']} style={{ gridColumn: '7 / span 2' }}>
      Address
    </HeaderLabel>
    <HeaderLabel variant="dest" fields={['dm']} style={{ gridColumn: '9 / span 2' }}>
      Dest Memo
    </HeaderLabel>
  </div>
);

// ---------------------------------------------------------------------------
// Per-leg gate resolver — calls each canEditX predicate once per leg with the
// adjacency / overlay / access inputs the predicates need. Result is a small
// bag of booleans the row + card layouts both consume.
// ---------------------------------------------------------------------------

interface LegGates {
  canOrig: boolean;
  canDest: boolean;
  canLegtype: boolean;
  canLegAction2: boolean;
  canLegDrop: boolean;
  /** Origin/destination appt DATE gates. On ORDERS these also drive the appt
   *  TIME RANGE beside them via canOrigApptTime/canDestApptTime below
   *  (deliberate: an appointment owned by scheduling shouldn't have its window
   *  moved either). Templates are the one place the two come apart. */
  canOrigAppt: boolean;
  canDestAppt: boolean;
  /** Appt TIME RANGE gates (appttime1 + appttime2 — the cell is gated as a
   *  unit). On orders these are exactly canOrigAppt/canDestAppt — unchanged
   *  behavior. On templates the DESTINATION window splits off as State 3: a
   *  delivery window is blueprint data that rides to every order built from the
   *  template, while the appt DATE stays locked. Origin stays locked either
   *  way (it's the live pickup, and it mirrors the previous leg's dest). */
  canOrigApptTime: boolean;
  canDestApptTime: boolean;
  /** Whether the clear (X) button beside each appt-date cell is enabled. On a
   *  scope locked by isApptLockedScope this is the only way to change the value,
   *  so it additionally requires every leg in the scope to be driver-blank. */
  canClearOrigAppt: boolean;
  canClearDestAppt: boolean;
  /** True when the PREVIOUS leg is the delivery leg AND is live (legDrop !== 'Y').
   *  Makes this leg's origApptDate the same physical date as the delivery leg's
   *  destApptDate, so clearing it must record the same reschedule row. */
  prevLegIsLiveDelivery: boolean;
  /** User holds `leg-appt-lock-override` — the appt lock doesn't apply to them. */
  apptLockOverride: boolean;
  /** Origin/destination appt-# gates. On orders these are the basic dispatch-state
   * gate (access + not paid), the same one legMiles and the memos use. The cell
   * has been on two wrong gates in turn:
   *   - the LOCATION gate: a paid previous leg locked the shared stop's location
   *     and the number with it (PRO 5147975, 2026-10-26 — leg 2's Orig Appt #
   *     greyed out with no date and no driver);
   *   - the APPT-DATE gate (the 2026-10-26 fix): the scheduling scope lock then
   *     locked the number on every pro with a saved appointment, so dispatch
   *     could no longer drop an off-hire ref # into it (ticket, 2026-10-27).
   * An appointment number is a reference, not a location and not an agreed
   * date; it feeds no driver pay and no cascade. Nothing but access and the
   * leg's own paid state should hold it. On templates they're always locked
   * (State 1 — appt # is a live/ops value). */
  canOrigApptNum: boolean;
  canDestApptNum: boolean;
  /** legMiles gate. On orders: editable post-delivery (dispatchers correct
   * mileage) unless the user lacks access or the leg is paid. On templates:
   * always locked (State 1 — mileage is a live actual, not a blueprint value). */
  canLegMiles: boolean;
  /** origmemo / legmemo gate (annotations on either side of the leg). On
   * orders: same access/paid rule as legMiles. On templates: always open
   * (State 3 — free annotation, editable regardless of approval). */
  canLegMemo: boolean;
}

function canEditDispatchState(
  leg: ProLeg, legEditingAccess: number, overrideActive: boolean = false,
): boolean {
  // The paid-leg lock is RECORD STATE, so Admin Override (#33501) releases it — the
  // driving case being a completed move whose pickup/stopoff labels were swapped: the
  // drivers' routes were right, only the labels are wrong, and the customer's milestones
  // depend on fixing them. There is no server-side paid-leg guard to match (v1's was
  // never ported), so this boolean is the whole lock.
  //
  // The PERMISSION half is untouched: legEditingAccess still has to be Edit. Override
  // releases what the record's state locked, never what the user's own grants withhold.
  return legEditingAccess >= 2 && (!leg.isPaid || overrideActive);
}

function resolveLegGates(
  leg: ProLeg,
  allLegs: ProLeg[],
  edits: LegEdits,
  legEditingAccess: number,
  canEditDeliveryLocation: boolean,
  apptClearAccess: number,
  apptLockOverrideAccess: number,
  /** `leg-appt-edit` level — see apptLockApplies. Lifts the scope lock only while
   *  the dispatch scope carries no driver. */
  apptEditAccess: number,
  /** SAVED legs (editing.originalLegs). The appt lock only applies to an
   *  appointment that was actually saved — see apptLockApplies. Must not be the
   *  effective list or a cell would re-lock as soon as the user picked a date. */
  originalLegs: ProLeg[] | undefined,
  deliveryLeg?: number,
  templateGate?: { isApproved: boolean; canEdit?: boolean },
  /** Admin Override (#33501), on for this pro right now. Releases the record-state leg
   *  locks (paid legs, saved-appointment locks). Templates never take this branch. */
  overrideActive: boolean = false,
): LegGates {
  // Template legs are gated by the field policy, not the order dispatch guards.
  // Templates carry no live legs (no drivers, no paid legs, no boundary
  // cascades), so the paid/DELIVERY/boundary locks don't apply — a field is
  // editable iff the policy says so for the current approval state. This also
  // implements FIELD_AUDIT rule #3: the delivery leg's destcode/legAction2
  // unlock while unapproved (State 2), because we never run the order-flow
  // DELIVERY lock here. `templateFieldEditable` returns false for any key not
  // in the policy map (default State 1), so appt/miles stay locked and only the
  // enumerated State-2/3 leg fields open. Orders never take this branch.
  if (templateGate) {
    // When the CSR/LC template-edit permission is absent, lock every leg field
    // (still the template branch, so it never falls through to order guards).
    const permitted = templateGate.canEdit ?? true;
    const canEdit = (key: string): boolean =>
      permitted && templateFieldEditable(key, { isApproved: templateGate.isApproved });
    return {
      canOrig: canEdit('origcode'),
      canDest: canEdit('destcode'),
      canLegtype: canEdit('legtype'),
      canLegAction2: canEdit('legAction2'),
      canLegDrop: canEdit('legDrop'),
      canOrigAppt: false, // State 1 — appt dates are live/ops
      canDestAppt: false, // State 1
      canOrigApptTime: false, // State 1 — mirrors the previous leg's dest window
      canDestApptTime: canEdit('destApptTime'), // State 3 — the delivery window
      canClearOrigAppt: false, // templates carry no live appointments to clear
      canClearDestAppt: false,
      prevLegIsLiveDelivery: false, // no reschedules on templates
      apptLockOverride: false, // nothing to override — templates lock appts outright
      canOrigApptNum: false, // State 1 — appt # is live/ops (location stays State 2)
      canDestApptNum: false, // State 1
      canLegMiles: false, // State 1 — mileage is a live actual (not in policy)
      canLegMemo: canEdit('legmemo'), // State 3 — free annotation
    };
  }

  const idx = allLegs.findIndex((l) => l.legrecno === leg.legrecno);
  const prevLeg = idx > 0 ? allLegs[idx - 1] : null;
  const nextLeg = idx >= 0 && idx < allLegs.length - 1 ? allLegs[idx + 1] : null;
  // Effective delivery legnum, resolved from the list this function walks.
  const effectiveDeliveryLeg = resolveDeliveryLegnum(allLegs, deliveryLeg);
  const canBasicEdit = canEditDispatchState(leg, legEditingAccess, overrideActive);
  // Both `leg` and `allLegs` are EFFECTIVE (overlaid) — ProDetail passes
  // `legs={editing?.effectiveLegs ?? ...}`. That's what the clear gate wants: a
  // staged clear should immediately disable the X.
  const effLeg = allLegs.find((l) => l.legrecno === leg.legrecno) ?? leg;
  // The SAVED value, by contrast, decides whether the appt lock applies at all.
  const savedLeg = originalLegs?.find((l) => l.legrecno === leg.legrecno);
  const savedApptDate = (side: 'orig' | 'dest'): string | null | undefined => {
    // `originalLegs` is optional on the editing prop. Without it we can't tell a
    // saved date from a staged one, so fall back to the EFFECTIVE value — that
    // errs toward LOCKED rather than silently dropping the protection.
    if (!originalLegs) {
      return side === 'orig' ? effLeg.origApptDate : effLeg.destApptDate;
    }
    // Supplied but no twin ⇒ this leg is new, so nothing was ever agreed.
    if (!savedLeg) return '';
    return side === 'orig' ? savedLeg.origApptDate : savedLeg.destApptDate;
  };
  // Pro override supersedes the role-level appt-lock override: a pro-admin-override
  // holder does not also have to pass the apptLockOverride grant. (Per Lee, 2026-09-11 —
  // "this override will override that override".) The role-level path is unchanged for
  // everyone else.
  const apptLockOverride = apptLockOverrideAccess >= 2 || overrideActive;
  // The warehouse-LC grant. Deliberately NOT folded into apptLockOverride: that one
  // ignores drivers, this one stops at the first one in the dispatch scope.
  const apptEdit = apptEditAccess >= 2;
  // Location gates — the appt-# cells ride with these on orders (unchanged).
  const canOrig = canEditOrigin({ leg, prevLeg, legEditingAccess, canEditDeliveryLocation, edits, overrideActive });
  const canDest = canEditDestination({ leg, nextLeg, legEditingAccess, canEditDeliveryLocation, edits, overrideActive });
  // The delivery leg's destApptDate used to be force-unlocked here so the
  // change could route through the required reschedule modal. The scope rule
  // supersedes that: a DELIVERY leg is in a DELIVERY scope, so the cell is
  // read-only and the modal is reached via the clear (X) button instead.
  const canOrigAppt = canEditOrigAppt({
    leg,
    prevLeg,
    allLegs,
    savedApptDate: savedApptDate('orig'),
    apptLockOverride,
    apptEdit,
    overrideActive,
  });
  const canDestAppt = canEditDestAppt({
    leg,
    nextLeg,
    allLegs,
    savedApptDate: savedApptDate('dest'),
    apptLockOverride,
    apptEdit,
    overrideActive,
  });
  return {
    canOrig,
    canDest,
    canLegtype: canEditLegtype({ leg, prevLeg, legEditingAccess, edits, overrideActive }),
    canLegAction2: canEditLegAction2({ leg, nextLeg, legEditingAccess, edits, overrideActive }),
    canLegDrop: canEditLegDrop({ legEditingAccess }),
    canOrigAppt,
    canDestAppt,
    // On orders the time range rides with its side's date gate — unchanged.
    canOrigApptTime: canOrigAppt,
    canDestApptTime: canDestAppt,
    canClearOrigAppt: canClearAppt({
      leg,
      allLegs,
      apptClearAccess,
      currentValue: effLeg.origApptDate,
      apptLockOverride,
      overrideActive,
    }),
    canClearDestAppt: canClearAppt({
      leg,
      allLegs,
      apptClearAccess,
      currentValue: effLeg.destApptDate,
      apptLockOverride,
      overrideActive,
    }),
    // prevLeg comes from allLegs (effective), so a staged Live/Drop flip on the
    // delivery leg immediately moves the reschedule requirement with it.
    prevLegIsLiveDelivery:
      effectiveDeliveryLeg !== undefined &&
      !!prevLeg &&
      prevLeg.legnum === effectiveDeliveryLeg &&
      prevLeg.legDrop !== 'Y',
    apptLockOverride,
    // Appt # is a reference: access + this leg not paid, like miles and memos.
    // Neither the location gate nor the appointment (scope) lock applies.
    canOrigApptNum: canBasicEdit,
    canDestApptNum: canBasicEdit,
    canLegMiles: canBasicEdit,
    canLegMemo: canBasicEdit,
  };
}

// ---------------------------------------------------------------------------
// Wide-layout single leg row
// ---------------------------------------------------------------------------

interface LegRowProps {
  leg: ProLeg;
  editing?: OrderEditingProp;
  gates: LegGates;
  /** True when the four arrival/depart datetime cells should be locked
   * (computed from `arrivalDepartAccess < 2` plus a sentinel for read-only). */
  arrivalDepartDisabled: boolean;
  /** Dispatch-scope membership for this leg — drives the multi-leg purple
   * border and the "Included" badge on non-last legs in scope. */
  scope: ScopeMembership;
  /** First leg in the order — gets the single visible cog for the
   * `leg-arrival-depart` permission so admins have a discoverable click
   * target. Other legs share the same componentId but hide their cogs. */
  isFirstLeg?: boolean;
  /** True when this leg may surface its origin-side "remove stop" button.
   * Mirrors the drawer's conditional render at
   * OrderDetailsModalContent.tsx ~3364. */
  canRemoveOriginStop?: boolean;
  /** True when this leg may surface its destination-side "remove stop"
   * button (last leg only). Drawer parity at ~3621. */
  canRemoveDestStop?: boolean;
}

const LegRow: React.FC<LegRowProps> = ({
  leg,
  editing,
  gates,
  arrivalDepartDisabled,
  scope,
  isFirstLeg = false,
  canRemoveOriginStop = false,
  canRemoveDestStop = false,
}) => {
  const oType = leg.legtype ?? '';
  const dType = leg.legAction2 ?? '';
  const legrecno = leg.legrecno;
  const isNew = legrecno < 0;
  const isPendingRemoved = !!editing?.pendingRemovedLegrecnos?.includes(legrecno);
  const isComplete = isLegComplete(leg);
  const { etaByLeg, marginByLeg, marginLimit, marginLoading } = React.useContext(OrderContextCtx);

  const ctxValue: LegEditingCtxValue = useMemo(
    () => ({ editing, legrecno, legnum: leg.legnum, isPendingRemoved, isNew, isComplete }),
    [editing, legrecno, leg.legnum, isPendingRemoved, isNew, isComplete],
  );

  const rowStateClass =
    (isNew ? 'bg-emerald-50 dark:bg-emerald-900/30 border-l-2 border-emerald-400 dark:border-emerald-600 ' : '') +
    (isPendingRemoved ? 'opacity-50 line-through ' : '');

  // Border for the row: single-leg scope keeps the existing 1px slate divider;
  // multi-leg scope draws a purple frame whose corners round only on the
  // first/last legs so the scope reads as a single grouped block.
  const scopeBorder = scopeBorderClass(scope, 'row');
  const baseBorder = scope.isMultiLegScope ? '' : 'border-b-2 border-slate-200 dark:border-gray-700';

  return (
    <LegEditingCtx.Provider value={ctxValue}>
      <div
        className={`grid ${baseBorder} ${scopeBorder} ${rowStateClass}`}
        style={{ gridTemplateColumns: GRID_TEMPLATE }}
        data-testid={`leg-row-${leg.legnum}`}
        data-scope-key={scope.scopeKey}
      >
        {/* # — spans 4 rows (3 stop rows + driver strip) */}
        <DataCell
          style={{ gridRow: '1 / span 4' }}
          className={`border-r-slate-200 dark:border-r-gray-700 justify-center font-bold text-[0.8125rem] flex-col gap-0.5 ${
            isComplete
              ? '!bg-slate-800 text-white'
              : '!bg-slate-50 dark:!bg-gray-800 text-slate-500 dark:text-gray-400'
          }`}
        >
          {leg.plannedWithSmartPreplan ? (
            <Tooltip content="Planned with Smart Pre Plan Tool" placement="right">
              <span
                className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-blue-500 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300"
                data-testid={`leg-smart-preplan-${leg.legnum}`}
              >
                {leg.legnum}
              </span>
            </Tooltip>
          ) : (
            <span>{leg.legnum}</span>
          )}
          {isNew && (
            <span
              className="mt-0.5 px-1 py-0 text-[0.5rem] font-bold uppercase tracking-wider rounded bg-emerald-500 text-white"
              data-testid={`leg-new-badge-${leg.legnum}`}
            >
              NEW
            </span>
          )}
          <LegConflictBadge compact />
          <LegApplyAllCheckbox leg={leg} />
        </DataCell>

        {/* Row 1: Move types + appt/date/time */}
        <DataCell field="ot">
          <CS
            field="legtype"
            value={oType}
            options={MOVE_TYPE_OPTIONS}
            badgeClass={moveTypeBadge(oType) + ' font-semibold'}
            ariaLabel={`Leg ${leg.legnum} origin move type`}
            disabled={!gates.canLegtype}
          />
        </DataCell>
        <DataCell field="oa">
          <CI
            field="origApptNumber"
            value={leg.origApptNumber ?? ''}
            mono
            placeholder="—"
            ariaLabel={`Leg ${leg.legnum} origin appt #`}
            disabled={!gates.canOrigApptNum}
          />
        </DataCell>
        <DataCell field="od">
          <LegDateCell
            field="origApptDate"
            value={leg.origApptDate}
            ariaLabel={`Leg ${leg.legnum} origin date`}
            disabled={!gates.canOrigAppt}
            canClear={gates.canClearOrigAppt}
            prevLegIsLiveDelivery={gates.prevLegIsLiveDelivery}
            clearAriaLabel={`Clear leg ${leg.legnum} origin appt`}
            apptMismatch={hasApptDriverDateMismatch(leg.origApptDate, [
              leg.origArrivalDateTime,
              leg.origDepartDateTime,
            ])}
            apptPastDue={hasPastApptWithoutDriverTimes(leg.origApptDate, [
              leg.origArrivalDateTime,
              leg.origDepartDateTime,
            ])}
          />
        </DataCell>
        <DataCell field="otm">
          <LegTimeRangeCell
            field1="origApptTime1"
            value1={leg.origApptTime1}
            field2="origApptTime2"
            value2={leg.origApptTime2}
            ariaLabel={`Leg ${leg.legnum} origin time`}
            disabled={!gates.canOrigApptTime}
          />
        </DataCell>
        {/* Splitter column — vertical divider + add-stopoff button, spans the
         * three stop rows. Sits between the origin (cols 2-5) and destination
         * (cols 7-10) blocks. */}
        <DataCell
          style={{ gridColumn: 6, gridRow: '1 / span 3' }}
          className="!p-0 !border-r-slate-200 dark:!border-r-gray-700 justify-center"
        >
          <LegSplitterButton leg={leg} />
        </DataCell>
        <DataCell field="dt" style={{ gridColumn: 7 }}>
          <CS
            field="legAction2"
            value={dType}
            options={MOVE_TYPE_OPTIONS}
            badgeClass={moveTypeBadge(dType) + ' font-semibold'}
            ariaLabel={`Leg ${leg.legnum} dest move type`}
            disabled={!gates.canLegAction2}
          />
        </DataCell>
        <DataCell field="da">
          <CI
            field="destApptNumber"
            value={leg.destApptNumber ?? ''}
            mono
            placeholder="—"
            ariaLabel={`Leg ${leg.legnum} dest appt #`}
            disabled={!gates.canDestApptNum}
          />
        </DataCell>
        <DataCell field="dd">
          <LegDateCell
            field="destApptDate"
            value={leg.destApptDate}
            ariaLabel={`Leg ${leg.legnum} dest date`}
            disabled={!gates.canDestAppt}
            canClear={gates.canClearDestAppt}
            clearAriaLabel={`Clear leg ${leg.legnum} dest appt`}
            apptMismatch={hasApptDriverDateMismatch(leg.destApptDate, [
              leg.destArrivalDateTime,
              leg.destDepartDateTime,
            ])}
            apptPastDue={hasPastApptWithoutDriverTimes(leg.destApptDate, [
              leg.destArrivalDateTime,
              leg.destDepartDateTime,
            ])}
          />
        </DataCell>
        <DataCell field="dtm">
          <LegTimeRangeCell
            field1="destApptTime1"
            value1={leg.destApptTime1}
            field2="destApptTime2"
            value2={leg.destApptTime2}
            ariaLabel={`Leg ${leg.legnum} dest time`}
            disabled={!gates.canDestApptTime}
          />
        </DataCell>

        {/* Right column — Drop / Status full-width, then Miles|ETA and
         * Pay|Margin as 50/50 pairs. Miles is a calculated value (read-only).
         * Move-state badges + Driver live in row 4's bottom bar. */}
        <DataCell
          field="det"
          style={{ gridColumn: 11, gridRow: '1 / span 3' }}
          className="flex-col gap-1 items-stretch"
        >
          <DetailRow label="Drop">
            <DropToggleBadge legDrop={leg.legDrop} disabled={!gates.canLegDrop} />
          </DetailRow>
          <DetailRow label="Status">
            <span
              className="h-5 px-1 text-[0.6875rem] text-slate-700 dark:text-gray-300 inline-flex items-center"
              aria-label={`Leg ${leg.legnum} status`}
            >
              {leg.legstatus ?? ''}
            </span>
          </DetailRow>
          {/* Miles (calculated, read-only). ETA times are NOT here — they ride
            * inline in the origin/destination name cells (one per location) so
            * each time reads against the place it belongs to. Only the
            * driver-level "Not started" state falls back to this stack. */}
          <DetailRow label="Miles">
            <span
              className="h-5 px-1 text-[0.6875rem] text-slate-700 dark:text-gray-300 inline-flex items-center"
              aria-label={`Leg ${leg.legnum} miles`}
            >
              {leg.legMiles ?? '—'}
            </span>
          </DetailRow>
          {etaByLeg?.get(leg.legnum)?.notStarted && (
            <DetailRow label="ETA">
              <span
                className="h-5 px-1 text-[0.6875rem] text-slate-400 dark:text-gray-500 inline-flex items-center"
                title="Driver has not started the day"
              >
                Not started
              </span>
            </DetailRow>
          )}
          {/* Estimated pay + mile-allocated margin, side by side. */}
          <div className="flex gap-3">
            <div className="flex-1 min-w-0">
              <DetailRow label="Pay">
                <LegPayCell pay={marginByLeg?.get(leg.legnum)} loading={marginLoading} />
              </DetailRow>
            </div>
            {SHOW_LEG_MARGIN && (
              <div className="flex-1 min-w-0">
                <DetailRow label="Margin">
                  <LegMarginCell
                    pay={marginByLeg?.get(leg.legnum)}
                    marginLimit={marginLimit}
                  />
                </DetailRow>
              </div>
            )}
          </div>
        </DataCell>

        {/* Trailing actions column — × / Undo, spans all 4 rows */}
        <DataCell
          style={{ gridColumn: 12, gridRow: '1 / span 4' }}
          className="!p-0.5 justify-center"
        >
          <LegActionButton leg={leg} editing={editing} />
        </DataCell>

        {/* Row 2: Origin name + arrival/depart | Dest name + arrival/depart */}
        <DataCell field="on" style={{ gridColumn: '2 / span 2' }}>
          <LegLocationBulkFlag fieldKey="pickupOrigin" leg={leg} editable={gates.canOrig}>
            <LegLocationCell
              side="orig"
              value={leg.origcode}
              storedName={leg.origName}
              ariaLabel={`Leg ${leg.legnum} origin name`}
              disabled={!gates.canOrig}
              isDeliveryStop={leg.legtype === 'DELIVERY'}
            />
          </LegLocationBulkFlag>
          <LocationInfoButton recno={leg.origcode} />
          {!leg.origcode && (
            <span className="text-blue-600 dark:text-blue-400 inline-flex shrink-0">
              <Icon.info className="w-3.5 h-3.5" />
            </span>
          )}
          <PermissionsContainer
            componentId="leg-add-remove-stops"
            componentName="Add/Remove Stops"
            showCog={false}
            defaultHidden={true}
          >
            <RemoveStopButton
              side="origin"
              legnum={leg.legnum}
              hidden={!canRemoveOriginStop}
            />
          </PermissionsContainer>
          <LegEtaChip eta={etaByLeg?.get(leg.legnum)} side="orig" legnum={leg.legnum} />
          <RouteEta moveStatus={etaByLeg?.get(leg.legnum)?.moveStatus} cascade={etaByLeg?.get(leg.legnum)?.routeCascade} side="origin" />
        </DataCell>
        <DataCell field="oar">
          <PermissionsContainer
            componentId="leg-arrival-depart"
            componentName="Arrival/Depart Times"
            showCog={isFirstLeg}
          >
            <LegDateTimeCell
              field="origArrivalDateTime"
              value={leg.origArrivalDateTime}
              ariaLabel={`Leg ${leg.legnum} origin arrival`}
              disabled={arrivalDepartDisabled}
            />
          </PermissionsContainer>
        </DataCell>
        <DataCell field="ode">
          <PermissionsContainer
            componentId="leg-arrival-depart"
            componentName="Arrival/Depart Times"
            showCog={false}
          >
            <LegDateTimeCell
              field="origDepartDateTime"
              value={leg.origDepartDateTime}
              ariaLabel={`Leg ${leg.legnum} origin departure`}
              disabled={arrivalDepartDisabled}
            />
          </PermissionsContainer>
        </DataCell>
        <DataCell field="dn" style={{ gridColumn: '7 / span 2' }}>
          <LegLocationBulkFlag fieldKey="terminateDest" leg={leg} editable={gates.canDest}>
            <LegLocationCell
              side="dest"
              value={leg.destcode}
              storedName={leg.destName}
              ariaLabel={`Leg ${leg.legnum} destination name`}
              disabled={!gates.canDest}
              isDeliveryStop={leg.legAction2 === 'DELIVERY'}
            />
          </LegLocationBulkFlag>
          <LocationInfoButton recno={leg.destcode} />
          {!leg.destcode && (
            <span className="text-blue-600 dark:text-blue-400 inline-flex shrink-0">
              <Icon.info className="w-3.5 h-3.5" />
            </span>
          )}
          <PermissionsContainer
            componentId="leg-add-remove-stops"
            componentName="Add/Remove Stops"
            showCog={false}
            defaultHidden={true}
          >
            <RemoveStopButton
              side="destination"
              legnum={leg.legnum}
              hidden={!canRemoveDestStop}
            />
          </PermissionsContainer>
          <LegEtaChip eta={etaByLeg?.get(leg.legnum)} side="dest" legnum={leg.legnum} />
          <RouteEta moveStatus={etaByLeg?.get(leg.legnum)?.moveStatus} data={etaByLeg?.get(leg.legnum)?.routeEta} cascade={etaByLeg?.get(leg.legnum)?.routeCascade} side="destination" />
        </DataCell>
        <DataCell field="dar">
          <PermissionsContainer
            componentId="leg-arrival-depart"
            componentName="Arrival/Depart Times"
            showCog={false}
          >
            <LegDateTimeCell
              field="destArrivalDateTime"
              value={leg.destArrivalDateTime}
              ariaLabel={`Leg ${leg.legnum} dest arrival`}
              disabled={arrivalDepartDisabled}
            />
          </PermissionsContainer>
        </DataCell>
        <DataCell field="dde">
          <PermissionsContainer
            componentId="leg-arrival-depart"
            componentName="Arrival/Depart Times"
            showCog={false}
          >
            <LegDateTimeCell
              field="destDepartDateTime"
              value={leg.destDepartDateTime}
              ariaLabel={`Leg ${leg.legnum} dest departure`}
              disabled={arrivalDepartDisabled}
            />
          </PermissionsContainer>
        </DataCell>

        {/* Row 3: Address (2 cols) | Memo (2 cols) | Address | Memo */}
        <DataCell field="oad" style={{ gridColumn: '2 / span 2' }}>
          <LegAddressDisplay
            value={leg.origCityState}
            recno={leg.origcode}
            ariaLabel={`Leg ${leg.legnum} origin address`}
          />
        </DataCell>
        <DataCell field="om" style={{ gridColumn: '4 / span 2' }}>
          <Tooltip content={toSentenceCase(leg.origmemo)} disabled={!leg.origmemo} placement="top">
            <CI
              field="origmemo"
              value={leg.origmemo ?? ''}
              placeholder="memo…"
              ariaLabel={`Leg ${leg.legnum} origin memo`}
              disabled={!gates.canLegMemo}
            />
          </Tooltip>
        </DataCell>
        <DataCell field="dad" style={{ gridColumn: '7 / span 2' }}>
          <LegAddressDisplay
            value={leg.destCityState}
            recno={leg.destcode}
            ariaLabel={`Leg ${leg.legnum} dest address`}
          />
        </DataCell>
        <DataCell field="dm" style={{ gridColumn: '9 / span 2' }}>
          <Tooltip content={toSentenceCase(leg.legmemo)} disabled={!leg.legmemo} placement="top">
            <CI
              field="legmemo"
              value={leg.legmemo ?? ''}
              placeholder="memo…"
              ariaLabel={`Leg ${leg.legnum} dest memo`}
              disabled={!gates.canLegMemo}
            />
          </Tooltip>
        </DataCell>

        {/* Row 4: bottom bar (Status + history + info + send-to-move + driver) */}
        <DataCell
          style={{ gridColumn: '2 / span 10', gridRow: 4 }}
          className="!bg-slate-50 dark:!bg-gray-800 !py-1 !px-2"
        >
          <LegBottomBar leg={leg} scope={scope} isFirstLeg={isFirstLeg} />
        </DataCell>
      </div>
    </LegEditingCtx.Provider>
  );
};

interface DetailRowProps {
  label: string;
  children: React.ReactNode;
}
const DetailRow: React.FC<DetailRowProps> = ({ label, children }) => (
  <div className="flex items-center gap-1.5">
    <span className="text-[0.59375rem] font-semibold uppercase tracking-wider text-slate-400 dark:text-gray-500 w-11 shrink-0">
      {label}
    </span>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// Per-row × / Undo button
// ---------------------------------------------------------------------------

interface LegActionButtonProps {
  leg: ProLeg;
  editing?: OrderEditingProp;
}

// Now that per-stop minus buttons live inside the orig/dest name cells (drawer
// parity), this row-end slot only surfaces the Undo affordance for legs that
// were marked pending-removed. Non-pending legs render nothing here.
const LegActionButton: React.FC<LegActionButtonProps> = ({ leg, editing }) => {
  const isPendingRemoved = !!editing?.pendingRemovedLegrecnos?.includes(leg.legrecno);
  if (!isPendingRemoved || !editing?.restoreLeg) return null;
  return (
    <PermissionsContainer
      componentId="leg-add-remove-stops"
      componentName="Add/Remove Stops"
      showCog={false}
      defaultHidden={true}
    >
      <button
        type="button"
        onClick={() => editing.restoreLeg?.(leg.legrecno)}
        className="text-[0.625rem] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 hover:text-emerald-900 dark:hover:text-emerald-200 px-1 py-0.5 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/30 no-underline"
        aria-label={`Undo remove leg ${leg.legnum}`}
        data-testid={`leg-undo-${leg.legnum}`}
      >
        Undo
      </button>
    </PermissionsContainer>
  );
};

// ---------------------------------------------------------------------------
// Cards layout (< 1280px)
// ---------------------------------------------------------------------------

interface LegFieldProps {
  label: string;
  full?: boolean;
  children: React.ReactNode;
}

const LegField: React.FC<LegFieldProps> = ({ label, full, children }) => (
  <div
    className="flex flex-col gap-0.5 min-w-0"
    style={full ? { gridColumn: '1 / -1' } : undefined}
  >
    <div className="text-[0.59375rem] font-semibold uppercase tracking-wider text-slate-400 dark:text-gray-500">
      {label}
    </div>
    {children}
  </div>
);

interface LegEndProps {
  side: 'o' | 'd';
  leg: ProLeg;
  stacked: boolean;
  gates: LegGates;
  arrivalDepartDisabled: boolean;
  /** True when this side may surface its inline "remove stop" button. Mirrors
   * the wide layout's canRemoveOriginStop/canRemoveDestStop gating. */
  canRemoveStop?: boolean;
}

const LegEnd: React.FC<LegEndProps> = ({ side, leg, stacked, gates, arrivalDepartDisabled, canRemoveStop = false }) => {
  const isOrig = side === 'o';
  const { etaByLeg, marginByLeg, marginLimit, marginLoading } = React.useContext(OrderContextCtx);
  const type = isOrig ? leg.legtype : leg.legAction2;
  const apptNum = isOrig ? leg.origApptNumber : leg.destApptNumber;
  const apptDate = isOrig ? leg.origApptDate : leg.destApptDate;
  const apptTime = isOrig ? leg.origApptTime1 : leg.destApptTime1;
  const apptTime2 = isOrig ? leg.origApptTime2 : leg.destApptTime2;
  const cityState = isOrig ? leg.origCityState : leg.destCityState;
  const arrival = isOrig ? leg.origArrivalDateTime : leg.destArrivalDateTime;
  const depart = isOrig ? leg.origDepartDateTime : leg.destDepartDateTime;
  const memo = isOrig ? leg.origmemo : leg.legmemo;
  const code = isOrig ? leg.origcode : leg.destcode;

  const fType = isOrig ? 'legtype' : 'legAction2';
  const fApptNum = isOrig ? 'origApptNumber' : 'destApptNumber';
  const fApptDate = isOrig ? 'origApptDate' : 'destApptDate';
  const fApptTime = isOrig ? 'origApptTime1' : 'destApptTime1';
  const fApptTime2 = isOrig ? 'origApptTime2' : 'destApptTime2';
  const fArrival = isOrig ? 'origArrivalDateTime' : 'destArrivalDateTime';
  const fDepart = isOrig ? 'origDepartDateTime' : 'destDepartDateTime';
  const fMemo = isOrig ? 'origmemo' : 'legmemo';

  // Gate selection for this side: origin uses canOrig + canLegtype + canOrigAppt;
  // destination uses canDest + canLegAction2 + canDestAppt. The memo cell
  // gets its own gate — annotations follow the basic dispatch-state gate, not
  // the location/appt boundary gates.
  const canLocation = isOrig ? gates.canOrig : gates.canDest;
  const canType = isOrig ? gates.canLegtype : gates.canLegAction2;
  const canAppt = isOrig ? gates.canOrigAppt : gates.canDestAppt;
  // The time range has its own gate — identical to canAppt on orders, but on a
  // template the destination window unlocks while the date beside it stays shut.
  const canApptTime = isOrig ? gates.canOrigApptTime : gates.canDestApptTime;
  // Appt # is the basic dispatch-state gate on orders (like miles and memos),
  // and is locked on templates (State 1) — see resolveLegGates.
  const canApptNum = isOrig ? gates.canOrigApptNum : gates.canDestApptNum;
  const canClear = isOrig ? gates.canClearOrigAppt : gates.canClearDestAppt;
  // Both origmemo and legmemo are annotation-only and follow the same
  // rule — open unless the user lacks access or the leg is paid (State 3 on
  // templates: always open).
  const canMemo = gates.canLegMemo;

  return (
    <div
      className={
        'rounded-md p-1.5 border ' +
        (isOrig ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-100 dark:border-blue-900/50' : 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700')
      }
    >
      <div
        className={
          'mb-1 flex items-center justify-between text-[0.625rem] font-bold uppercase tracking-wider ' +
          (isOrig ? 'text-blue-700 dark:text-blue-300' : 'text-amber-800 dark:text-amber-300')
        }
      >
        <span>{isOrig ? 'Origin' : 'Destination'}</span>
        {!code && (
          <span className="text-blue-600 dark:text-blue-400 inline-flex">
            <Icon.info className="w-3.5 h-3.5" />
          </span>
        )}
      </div>
      <div
        className="grid gap-1.5"
        style={{ gridTemplateColumns: stacked ? '1fr' : '1fr 1fr' }}
      >
        <LegField label="Move Type" full>
          <CS
            field={fType}
            value={type ?? ''}
            options={MOVE_TYPE_OPTIONS}
            badgeClass={moveTypeBadge(type) + ' font-semibold'}
            disabled={!canType}
          />
        </LegField>
        <LegField label="Appt #">
          <CI
            field={fApptNum}
            value={apptNum ?? ''}
            mono
            placeholder="—"
            disabled={!canApptNum}
          />
        </LegField>
        <LegField label="Date">
          <LegDateCell
            field={fApptDate}
            value={apptDate}
            ariaLabel={`Leg ${leg.legnum} ${isOrig ? 'origin' : 'dest'} date`}
            disabled={!canAppt}
            canClear={canClear}
            prevLegIsLiveDelivery={isOrig && gates.prevLegIsLiveDelivery}
            clearAriaLabel={`Clear leg ${leg.legnum} ${isOrig ? 'origin' : 'dest'} appt`}
            apptMismatch={hasApptDriverDateMismatch(apptDate, [arrival, depart])}
            apptPastDue={hasPastApptWithoutDriverTimes(apptDate, [arrival, depart])}
          />
        </LegField>
        <LegField label="Time">
          <LegTimeRangeCell
            field1={fApptTime}
            value1={apptTime}
            field2={fApptTime2}
            value2={apptTime2}
            ariaLabel={`Leg ${leg.legnum} ${isOrig ? 'origin' : 'dest'} time`}
            disabled={!canApptTime}
          />
        </LegField>
        {/* Driver ETA — one per side: this card's own location. Origin cards go
         * blank once the driver has reached the origin. Estimated pay/margin
         * stay destination-side only (one value per leg). */}
        <LegField label="ETA">
          <div className="min-h-5 inline-flex flex-wrap items-center gap-x-2">
            <LegEtaCell eta={etaByLeg?.get(leg.legnum)} side={isOrig ? 'orig' : 'dest'} />
            <RouteEta moveStatus={etaByLeg?.get(leg.legnum)?.moveStatus} data={etaByLeg?.get(leg.legnum)?.routeEta} cascade={etaByLeg?.get(leg.legnum)?.routeCascade} side={isOrig ? 'origin' : 'destination'} />
          </div>
        </LegField>
        {!isOrig && (
          <LegField label="Pay">
            <div className="h-5 inline-flex items-center">
              <LegPayCell pay={marginByLeg?.get(leg.legnum)} />
            </div>
          </LegField>
        )}
        {SHOW_LEG_MARGIN && !isOrig && (
          <LegField label="Margin">
            <div className="h-5 inline-flex items-center">
              <LegMarginCell
                pay={marginByLeg?.get(leg.legnum)}
                marginLimit={marginLimit}
              />
            </div>
          </LegField>
        )}
        <LegField label={isOrig ? 'Origin' : 'Destination'} full>
          <div className="flex items-center gap-1 w-full">
            <div className="flex-1 min-w-0">
              <LegLocationCell
                side={isOrig ? 'orig' : 'dest'}
                value={code}
                storedName={isOrig ? leg.origName : leg.destName}
                ariaLabel={`Leg ${leg.legnum} ${isOrig ? 'origin' : 'destination'} name`}
                disabled={!canLocation}
                isDeliveryStop={isOrig ? leg.legtype === 'DELIVERY' : leg.legAction2 === 'DELIVERY'}
              />
            </div>
            <LocationInfoButton recno={code} />
            <PermissionsContainer
              componentId="leg-add-remove-stops"
              componentName="Add/Remove Stops"
              showCog={false}
              defaultHidden={true}
            >
              <RemoveStopButton
                side={isOrig ? 'origin' : 'destination'}
                legnum={leg.legnum}
                hidden={!canRemoveStop}
              />
            </PermissionsContainer>
          </div>
        </LegField>
        <LegField label="Address" full>
          <LegAddressDisplay
            value={cityState}
            recno={code}
            ariaLabel={`Leg ${leg.legnum} ${isOrig ? 'origin' : 'destination'} address`}
          />
        </LegField>
        <LegField label="Arrival">
          <PermissionsContainer
            componentId="leg-arrival-depart"
            componentName="Arrival/Depart Times"
            showCog={false}
          >
            <LegDateTimeCell field={fArrival} value={arrival} disabled={arrivalDepartDisabled} />
          </PermissionsContainer>
        </LegField>
        <LegField label="Departure">
          <PermissionsContainer
            componentId="leg-arrival-depart"
            componentName="Arrival/Depart Times"
            showCog={false}
          >
            <LegDateTimeCell field={fDepart} value={depart} disabled={arrivalDepartDisabled} />
          </PermissionsContainer>
        </LegField>
        <LegField label="Memo" full>
          <Tooltip content={toSentenceCase(memo)} disabled={!memo} placement="top">
            <CI
              field={fMemo}
              value={memo ?? ''}
              placeholder="memo…"
              color="#92400e"
              disabled={!canMemo}
            />
          </Tooltip>
        </LegField>
      </div>
    </div>
  );
};

interface LegsCardsProps {
  legs: ProLeg[];
  stacked: boolean;
  editing?: OrderEditingProp;
  legEditingAccess: number;
  /** Raw permission level. Per-leg disabled state (which also locks on paid)
   *  is computed inside via canEditArrivalDepart. */
  arrivalDepartAccess: number;
  /** `leg-delivery-location` permission — gates a DELIVERY leg's location cell. */
  canEditDeliveryLocation: boolean;
  /** `leg-appt-clear` permission — gates the X beside each appt-date cell. */
  apptClearAccess: number;
  /** `leg-appt-lock-override` level. */
  apptLockOverrideAccess: number;
  /** `leg-appt-edit` level — the warehouse-LC appointment grant. */
  apptEditAccess: number;
  /** Order's delivery legnum — lets resolveLegGates mark which legs sit on the
   *  delivery boundary, so clearing their appt date routes through the required
   *  reschedule modal. */
  deliveryLeg?: number;
  /** Template mode: policy-driven, approval-aware leg gates. Undefined for
   *  orders (keeps the dispatch-guard path). */
  templateGate?: { isApproved: boolean; canEdit?: boolean };
}

const LegsCards: React.FC<LegsCardsProps> = ({
  legs,
  stacked,
  editing,
  legEditingAccess,
  arrivalDepartAccess,
  canEditDeliveryLocation,
  apptClearAccess,
  apptLockOverrideAccess,
  apptEditAccess,
  deliveryLeg,
  templateGate,
}) => {
  // Admin Override (#33501) releases the record-state leg locks (paid legs,
  // saved-appointment locks). Read here rather than passed down so the card and grid
  // layouts cannot drift apart on which legs are editable.
  const { active: adminOverrideActive } = useAdminOverride();
  return (
  <div
    className="p-2 flex flex-col gap-2"
    data-testid="legs-cards"
  >
    {legs.length > 0 && (
      <PermissionsContainer
        componentId="leg-add-remove-stops"
        componentName="Add/Remove Stops"
        defaultHidden={true}
        showCog={true}
      >
        <AddLegPlaceholder
          onClick={() => editing?.addLeg?.('first', 0)}
          data-testid={`add-leg-before-${legs[0].legnum}`}
        />
      </PermissionsContainer>
    )}
    {legs.map((leg, i) => {
      const isNew = leg.legrecno < 0;
      const isPendingRemoved = !!editing?.pendingRemovedLegrecnos?.includes(leg.legrecno);
      const isComplete = isLegComplete(leg);
      const ctxValue: LegEditingCtxValue = {
        editing,
        legrecno: leg.legrecno,
        legnum: leg.legnum,
        isPendingRemoved,
        isNew,
        isComplete,
      };
      const allLegsForGates = editing?.effectiveLegs ?? legs;
      const editsForGates = editing?.editedLegs ?? {};
      const gates = resolveLegGates(leg, allLegsForGates, editsForGates, legEditingAccess, canEditDeliveryLocation, apptClearAccess, apptLockOverrideAccess, apptEditAccess, editing?.originalLegs, deliveryLeg, templateGate, adminOverrideActive);
      const scope = resolveScopeMembership(leg, allLegsForGates);
      // Arrival/depart are live actuals — always locked on templates (State 1).
      const arrivalDepartDisabled =
        !canEditArrivalDepart({ leg, arrivalDepartAccess, overrideActive: adminOverrideActive })
        || !!templateGate;
      // overflow-hidden on multi-leg cards would clip the purple side borders
      // visually connecting to the next leg, so only round the corners — let
      // the inner header's bg-slate-50 bleed naturally for single-leg cards.
      const scopeBorder = scopeBorderClass(scope, 'card');
      // Drawer-faithful "remove stop" button visibility — mirrors the wide
      // layout's per-leg gating (OrderDetailsModalContent.tsx ~3364 / ~3621).
      const multi = legs.length > 1;
      const prev = i > 0 ? legs[i - 1] : null;
      const canRemoveOriginStop =
        multi &&
        !leg.isPaid &&
        !prev?.isPaid &&
        !(prev && prev.legAction2 === 'DELIVERY');
      const canRemoveDestStop =
        i === legs.length - 1 &&
        multi &&
        !leg.isPaid &&
        leg.legAction2 !== 'DELIVERY';
      const cardStateClass =
        (isNew ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-400 dark:border-emerald-600 ' : 'bg-white dark:bg-gray-900 ') +
        (isPendingRemoved ? 'opacity-50 line-through ' : '');
      return (
        <React.Fragment key={leg.legrecno ?? leg.legnum}>
          <LegEditingCtx.Provider value={ctxValue}>
            <div
              className={`overflow-hidden ${scopeBorder} ${cardStateClass}`}
              data-testid={`leg-card-${leg.legnum}`}
              data-scope-key={scope.scopeKey}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap px-2 py-1 bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  {leg.plannedWithSmartPreplan ? (
                    <Tooltip content="Planned with Smart Pre Plan Tool" placement="top">
                      <span
                        className="text-[0.6875rem] font-bold text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/50 border border-blue-500 px-1.5 py-0.5 rounded-full"
                        data-testid={`leg-smart-preplan-${leg.legnum}`}
                      >
                        Leg {leg.legnum}
                      </span>
                    </Tooltip>
                  ) : (
                    <span className={`text-[0.6875rem] font-bold px-1.5 py-0.5 rounded-full ${
                      isComplete
                        ? 'bg-slate-800 text-white'
                        : 'text-slate-600 dark:text-gray-300 bg-slate-200 dark:bg-gray-700'
                    }`}>
                      Leg {leg.legnum}
                    </span>
                  )}
                  {isNew && (
                    <span
                      className="text-[0.5625rem] font-bold uppercase tracking-wider rounded bg-emerald-500 text-white px-1.5 py-0.5"
                      data-testid={`leg-new-badge-${leg.legnum}`}
                    >
                      NEW
                    </span>
                  )}
                  <LegConflictBadge />
                  <LegStateBadges leg={leg} scope={scope} />
                  <LegApplyAllCheckbox leg={leg} />
                  <span className="text-[0.71875rem] text-slate-500 dark:text-gray-400">
                    <span className="text-slate-900 dark:text-gray-100 font-semibold">
                      {leg.legMiles ?? '—'}
                    </span>{' '}
                    mi
                  </span>
                  <DropToggleBadge legDrop={leg.legDrop} disabled={!gates.canLegDrop} />
                </div>
                <div className="flex items-center gap-1.5">
                  <LegActionButton leg={leg} editing={editing} />
                </div>
              </div>
              <div
                className="p-2 grid gap-2"
                style={{
                  gridTemplateColumns: stacked ? '1fr' : '1fr 24px 1fr',
                  gridTemplateRows: stacked ? 'auto auto auto' : 'auto',
                }}
              >
                <LegEnd
                  side="o"
                  leg={leg}
                  stacked={stacked}
                  gates={gates}
                  arrivalDepartDisabled={arrivalDepartDisabled}
                  canRemoveStop={canRemoveOriginStop}
                />
                {stacked ? (
                  <LegSplitterButton leg={leg} orientation="horizontal" />
                ) : (
                  <LegSplitterButton leg={leg} orientation="vertical" />
                )}
                <LegEnd
                  side="d"
                  leg={leg}
                  stacked={stacked}
                  gates={gates}
                  arrivalDepartDisabled={arrivalDepartDisabled}
                  canRemoveStop={canRemoveDestStop}
                />
              </div>
              {/* Bottom bar — Status + history + info + send-to-move + driver */}
              <div className="px-2 py-1 bg-slate-50 dark:bg-gray-800 border-t border-slate-200 dark:border-gray-700">
                <LegBottomBar leg={leg} scope={scope} isFirstLeg={i === 0} />
              </div>
            </div>
          </LegEditingCtx.Provider>
        </React.Fragment>
      );
    })}
    {legs.length > 0 && (
      <PermissionsContainer
        componentId="leg-add-remove-stops"
        componentName="Add/Remove Stops"
        defaultHidden={true}
        showCog={false}
      >
        <AddLegPlaceholder
          onClick={() => editing?.addLeg?.('last', legs.length)}
          data-testid="add-leg-after-last"
        />
      </PermissionsContainer>
    )}
  </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const LegsGrid: React.FC<LegsGridProps> = ({
  legs,
  forceLayout,
  editing,
  pronumber,
  customerName,
  hazmat,
  isDaycab,
  isTank,
  overweightPermitRequired,
  moveType,
  reuseContainerPro,
  isDrop,
  dropReleaseDatetime,
  deliveryLeg,
  originateLeg,
  terminateLeg,
  billTo,
  consigneeName,
  firstRcv,
  warnings,
  openTtCount,
  openTickets,
  offhire,
  redeliveryExpirationDate,
  openRoCount,
  outsideCarrierDriver,
  driverValidationSnapshotRef,
  overrideApprovals,
  etaByLeg,
  marginByLeg,
  marginLimit,
  marginLoading,
  isTemplate = false,
  isApproved = false,
  canEditTemplate = true,
}) => {
  // Admin Override (#33501): releases the record-state leg locks — a paid leg, a saved
  // appointment — for this pro only. Never reaches the template branch below, which is
  // governed by the field policy instead.
  const { active: adminOverrideActive } = useAdminOverride();
  // On templates, per-leg gates come from the field policy (approval-aware),
  // further gated by the CSR/LC template-edit permission; orders pass
  // `undefined` and keep the dispatch-guard path unchanged.
  const templateGate = isTemplate ? { isApproved, canEdit: canEditTemplate } : undefined;

  // Delivery-location → Consignee copy prompt (FIELD_AUDIT rule #4): when the
  // delivery leg's destination changes, offer to copy it into the order/template
  // consignee. One-directional; editing the consignee itself does nothing here.
  const [consigneeCopyPrompt, setConsigneeCopyPrompt] = useState<
    { recno: number; name: string } | null
  >(null);
  // Effective consignee name (a pending edit wins over the saved prop) held in a
  // ref so the stable-identity handler below reads the freshest value without
  // re-creating on every consignee change.
  const effectiveConsigneeName =
    (editing?.editedOrderFields?.consigneeName as string | undefined) ??
    consigneeName ??
    '';
  const effectiveConsigneeNameRef = useRef(effectiveConsigneeName);
  effectiveConsigneeNameRef.current = effectiveConsigneeName;
  const handleDeliveryDestChange = useCallback((recno: number, name: string) => {
    if (!recno) return;
    // Skip the "Update Consignee?" prompt when the new delivery location already
    // matches the current consignee — there's nothing to change.
    const current = String(effectiveConsigneeNameRef.current || '').trim().toUpperCase();
    const incoming = String(name || '').trim().toUpperCase();
    if (current && current === incoming) return;
    setConsigneeCopyPrompt({ recno, name });
  }, []);

  const [hField, setHField] = useState<HoveredField>(null);
  const [travelHistoryModal, setTravelHistoryModal] = useState<{
    isOpen: boolean;
    legNum: number | null;
  }>({ isOpen: false, legNum: null });
  const travelHistoryCtx = useMemo<TravelHistoryCtxValue>(
    () => ({
      openTravelHistory: (legNum: number) =>
        setTravelHistoryModal({ isOpen: true, legNum }),
    }),
    [],
  );
  const [dispatchInfoLeg, setDispatchInfoLeg] = useState<ProLeg | null>(null);
  const dispatchInfoCtx = useMemo<DispatchInfoCtxValue>(
    () => ({
      openDispatchInfo: (leg: ProLeg) => setDispatchInfoLeg(leg),
    }),
    [],
  );
  const [sendDispatchLeg, setSendDispatchLeg] = useState<ProLeg | null>(null);
  const sendDispatchCtx = useMemo<SendDispatchCtxValue>(
    () => ({
      openSendDispatch: (leg: ProLeg) => setSendDispatchLeg(leg),
    }),
    [],
  );
  // Pending clear-driver confirmation. The cell hands us a `revert` to invoke
  // if the user backs out, so the optimistic UI snap is undone cleanly.
  const [clearDriverConfirm, setClearDriverConfirm] = useState<
    { legrecno: number; revert: () => void } | null
  >(null);
  const clearDriverCtx = useMemo<ClearDriverCtxValue>(
    () => ({
      requestClearDriver: (legrecno: number, revert: () => void) =>
        setClearDriverConfirm({ legrecno, revert }),
    }),
    [],
  );
  const wrapRef = useRef<HTMLDivElement>(null);
  // Default to wide so SSR / first render before ResizeObserver fires looks
  // like a desktop. Matches the prototype's initial state.
  const [width, setWidth] = useState<number>(2000);

  // Permission access levels — all three predicates need legEditingAccess at
  // minimum. arrivalDepartAccess gates the four datetime cells separately.
  // canEditDeliveryLocation gates a DELIVERY leg's location cell specifically.
  const {
    legEditingAccess,
    arrivalDepartAccess,
    canEditDeliveryLocation,
    apptClearAccess,
    apptLockOverrideAccess,
    apptEditAccess,
  } = useOrderFormContext();
  // arrivalDepartAccess is passed down to LegRow/LegsCards which compute the
  // per-leg disabled state via canEditArrivalDepart (also locks on isPaid).

  // Leg origin/destination location options are NOT preloaded — each
  // LegLocationCell runs a live server search (LocationTypeAhead), so a
  // location created mid-session is findable immediately.

  // Effective leg list (post-edits) is what the dispatch-scope calc and the
  // driver-validation context should reflect. Falls back to the raw `legs`
  // prop when `editing` isn't wired (read-only callers / tests).
  const allLegs = editing?.effectiveLegs ?? legs;
  // Effective dropReleaseDatetime — the order-level edit overrides the loaded
  // value so the drop-release warning reflects an unsaved edit immediately.
  const effectiveDropRelease =
    editing?.editedOrderFields?.dropReleaseDatetime !== undefined
      ? (editing.editedOrderFields.dropReleaseDatetime as string | undefined)
      : dropReleaseDatetime;
  // Stable key derived from the edited fields that affect driver
  // qualification. When this string changes, DriverSelectorCells re-validate
  // through their own logic — single source of truth, no duplicate
  // validation path in OrderFormView.
  //
  // legDrop changes scope composition (legs combine / split), which directly
  // affects the qualification check's set of locations and counties — so a
  // per-leg `legrecno:legDrop` digest is folded into the key. Flipping the
  // toggle either direction bumps the key, forcing every driver cell to
  // re-run validation against the new scope shape.
  const driverRevalidationKey = useMemo(() => {
    const overrides = buildOrderOverridesForValidation(editing?.editedOrderFields);
    const legDrops = allLegs
      .map((l) => `${l.legrecno}:${l.legDrop ?? ''}`)
      .join(',');
    return JSON.stringify({ overrides: overrides ?? null, legDrops });
  }, [editing?.editedOrderFields, allLegs]);

  // Per-leg validation result aggregator. DriverSelectorCells call
  // `recordLegValidation` on every onValidationChange; we keep a flat
  // {legnum → result} map and mirror it into `driverValidationSnapshotRef`
  // so ProDetailContainer's save handler can read the latest snapshot
  // synchronously at click-time. The save block is gone — invalid drivers
  // on incomplete legs now route through the override-approval modal at
  // save time (handled in ProDetailContainer).
  const [legValidationMap, setLegValidationMap] = useState<DriverValidationSnapshot>({});
  const recordLegValidation = useCallback(
    (legnum: number, isValid: boolean | null, missing?: string[]) => {
      // `null` = "not yet known" (loading / cleared). Treat the same as
      // valid — only confirmed `false` should trigger the override prompt.
      const effectiveValid = isValid === false ? false : true;
      setLegValidationMap((prev) => {
        const existing = prev[legnum];
        const nextMissing = missing ?? [];
        if (
          existing &&
          existing.isValid === effectiveValid &&
          existing.missing.length === nextMissing.length &&
          existing.missing.every((v, i) => v === nextMissing[i])
        ) {
          return prev; // No-op when nothing changed (avoid render churn)
        }
        return { ...prev, [legnum]: { isValid: effectiveValid, missing: nextMissing } };
      });
    },
    [],
  );

  // Mirror the validation map into the parent-owned ref every render. The
  // ref is the only synchronous read path the save handler has — useState
  // updates don't propagate through React props/context to a click handler
  // that already started its work.
  if (driverValidationSnapshotRef) {
    driverValidationSnapshotRef.current = legValidationMap;
  }

  // Approval lookup table keyed by `${legrecno}:${driver}`. The service
  // returns rows newest-first, so the first encounter for any key is the
  // latest approval — that's what we display in the leg indicator tooltip.
  const overrideApprovalByKey = useMemo(() => {
    const map = new Map<string, OverrideApprovalRecord>();
    if (overrideApprovals) {
      for (const a of overrideApprovals) {
        if (a.legrecno == null) continue;
        const key = `${a.legrecno}:${a.driver}`;
        if (!map.has(key)) map.set(key, a);
      }
    }
    return map;
  }, [overrideApprovals]);

  const legAddBlock = useMemo(
    () => streetTurnLegBlock(moveType, reuseContainerPro),
    [moveType, reuseContainerPro],
  );

  const orderCtx = useMemo<OrderContextCtxValue>(
    () => ({
      pronumber,
      isTemplate,
      customerName,
      hazmat,
      isDaycab,
      isTank,
      overweightPermitRequired,
      allLegs,
      moveType,
      legAddBlock,
      isDrop,
      dropReleaseDatetime: effectiveDropRelease,
      deliveryLeg,
      originateLeg,
      terminateLeg,
      billTo,
      onDeliveryDestChange: handleDeliveryDestChange,
      firstRcv,
      warnings,
      openTtCount,
      openTickets,
      offhire: (editing?.editedOrderFields?.offhire as string | undefined) ?? offhire,
      redeliveryExpirationDate: (editing?.editedOrderFields?.redeliveryExpirationDate as string | undefined) ?? redeliveryExpirationDate,
      openRoCount,
      outsideCarrierDriver,
      driverRevalidationKey,
      recordLegValidation,
      overrideApprovalByKey,
      etaByLeg,
      marginByLeg,
      marginLimit,
      marginLoading,
    }),
    [
      pronumber,
      isTemplate,
      customerName,
      hazmat,
      isDaycab,
      isTank,
      overweightPermitRequired,
      allLegs,
      moveType,
      legAddBlock,
      isDrop,
      effectiveDropRelease,
      deliveryLeg,
      originateLeg,
      terminateLeg,
      billTo,
      handleDeliveryDestChange,
      firstRcv,
      warnings,
      openTtCount,
      openTickets,
      offhire,
      redeliveryExpirationDate,
      editing?.editedOrderFields?.offhire,
      editing?.editedOrderFields?.redeliveryExpirationDate,
      openRoCount,
      outsideCarrierDriver,
      driverRevalidationKey,
      recordLegValidation,
      overrideApprovalByKey,
      etaByLeg,
      marginByLeg,
      marginLimit,
      marginLoading,
    ],
  );

  useEffect(() => {
    if (!wrapRef.current) return;
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  // Force a width re-measure whenever permissions mode toggles. Entering
  // permissions mode wraps the grid in an extra <div> (PermissionConfigurable)
  // which can leave the cached width stale and collapse the layout to cards
  // or stacked. Re-measuring on the toggle restores the correct breakpoint.
  const { permissionsMode } = usePermissionsMode();
  useEffect(() => {
    if (wrapRef.current) {
      setWidth(wrapRef.current.getBoundingClientRect().width);
    }
  }, [permissionsMode]);

  // forceLayout overrides width-driven mode; useful for tests + future
  // story-book/preview wiring without the ResizeObserver dance.
  const layout: 'wide' | 'cards' | 'stacked' =
    forceLayout ??
    (width < 560 ? 'stacked' : width < 1280 ? 'cards' : 'wide');
  const cards = layout !== 'wide';
  const stacked = layout === 'stacked';

  // ── Per-location detail (info icon) ──────────────────────────────────────
  // Detail for every location referenced by the legs, keyed by recno. Fetched
  // in one batched call on load and whenever a new code appears (e.g. the user
  // picks a location for a stop) — the effect keys off the set of codes in the
  // effective legs, so a freshly-picked location's icon shows as soon as its
  // detail returns. fetchedLocationRecnosRef dedupes / marks in-flight.
  const [locationInfoMap, setLocationInfoMap] = useState<Record<number, LocationInfo>>({});
  const [locationInfoRecno, setLocationInfoRecno] = useState<number | null>(null);
  const fetchedLocationRecnosRef = useRef<Set<number>>(new Set());

  const locationRecnos = useMemo(() => {
    const s = new Set<number>();
    for (const l of allLegs) {
      if (l.origcode) s.add(Number(l.origcode));
      if (l.destcode) s.add(Number(l.destcode));
    }
    return Array.from(s).filter((n) => Number.isFinite(n) && n > 0);
  }, [allLegs]);
  const locationRecnoKey = useMemo(
    () => locationRecnos.slice().sort((a, b) => a - b).join(','),
    [locationRecnos],
  );

  useEffect(() => {
    const missing = locationRecnos.filter((r) => !fetchedLocationRecnosRef.current.has(r));
    if (missing.length === 0) return;
    // Mark in-flight up front so rapid re-renders don't refetch the same recnos.
    missing.forEach((r) => fetchedLocationRecnosRef.current.add(r));
    api
      .get<Record<string, LocationInfo>>('/admin/locations/details', {
        params: { recnos: missing.join(',') },
      })
      .then((res) => {
        const data = res.data || {};
        setLocationInfoMap((prev) => {
          const next = { ...prev };
          for (const k of Object.keys(data)) next[Number(k)] = data[k];
          return next;
        });
      })
      .catch((error: { name?: string; code?: string }) => {
        // Allow a later retry on failure (leave cancels marked — they were superseded).
        if (error?.name !== 'AbortError' && error?.code !== 'ERR_CANCELED') {
          missing.forEach((r) => fetchedLocationRecnosRef.current.delete(r));
        }
        console.error('Error fetching location details:', error);
      });
    // locationRecnos is recomputed from locationRecnoKey; depend on the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationRecnoKey]);

  const locationInfoCtxValue = useMemo<LocationInfoCtxValue>(
    () => ({
      getLocationInfo: (recno) => (recno ? locationInfoMap[Number(recno)] : undefined),
      openLocationInfo: (recno) => setLocationInfoRecno(recno),
    }),
    [locationInfoMap],
  );

  return (
    <OrderContextCtx.Provider value={orderCtx}>
    <LocationInfoCtx.Provider value={locationInfoCtxValue}>
    <TravelHistoryCtx.Provider value={travelHistoryCtx}>
    <DispatchInfoCtx.Provider value={dispatchInfoCtx}>
    <SendDispatchCtx.Provider value={sendDispatchCtx}>
    <ClearDriverCtx.Provider value={clearDriverCtx}>
    {/* wrapRef sits OUTSIDE PermissionsContainer so its DOM identity stays
     * stable across permissionsMode toggles. Without this, entering perm
     * mode causes PermissionConfigurable to inject a wrapper above wrapRef
     * which remounts the subtree and produces a stale ResizeObserver
     * measurement — collapsing the layout to cards/stacked on first entry. */}
    <div
      ref={wrapRef}
      className="bg-sunken rounded-md border border-slate-200 dark:border-gray-700"
      data-testid="legs-grid"
    >
    <PermissionsContainer
      componentId="leg-editing"
      componentName="Leg Editing"
      className="[&>div.absolute]:!-top-10"
    >
      {/* Dark header */}
      <div className="bg-slate-800 dark:bg-gray-800 text-white px-3 py-2 rounded-t-md flex items-center justify-between">
        <span className="text-[0.78125rem] font-semibold uppercase tracking-wider inline-flex items-center gap-2">
          <Icon.truck className="w-3.5 h-3.5" />
          Legs
        </span>
        {/* Config-only anchor so the permissions cog for editing DELIVERY-leg
            locations has a home. The actual per-cell gating reads
            canEditDeliveryLocation from OrderFormContext (same split as
            leg-editing: anchored here for the cog, enforced via context). Ships
            defaultDisabled — an admin grants a group Edit to unlock it. Renders
            nothing for end users. */}
        <PermissionsContainer
          componentId="leg-delivery-location"
          componentName="Edit Delivery Leg Location"
          defaultDisabled
        >
          <span className="sr-only">Edit delivery leg location permission</span>
        </PermissionsContainer>
        {/* Config-only anchors for the two appointment permissions, same split as
            leg-delivery-location: the cog lives here, enforcement reads
            apptClearAccess / apptLockOverrideAccess from OrderFormContext.
            They CANNOT be anchored to an appt-date cell — LegDateCell
            early-returns to read-only text on a complete leg, so on an order
            whose legs are all run there'd be no cog anywhere and the permission
            would be ungrantable. This header always renders. */}
        <PermissionsContainer
          componentId="leg-appt-clear"
          componentName="Clear Appointment Date"
          defaultDisabled
        >
          <span className="sr-only">Clear appointment date permission</span>
        </PermissionsContainer>
        <PermissionsContainer
          componentId="leg-appt-lock-override"
          componentName="Edit Locked Appointment Dates"
          defaultDisabled
        >
          <span className="sr-only">Edit locked appointment dates permission</span>
        </PermissionsContainer>
        {/* The warehouse-LC grant. Narrower than leg-appt-lock-override beside it:
            it lifts the same PICKUP/DELIVERY lock, but only while nothing in the
            dispatch scope has a driver — so an LC can move an appointment they set
            until the move is dispatched, and not after. */}
        <PermissionsContainer
          componentId="leg-appt-edit"
          componentName="Edit Appointment Dates Before Dispatch"
          defaultDisabled
        >
          <span className="sr-only">Edit appointment dates before dispatch permission</span>
        </PermissionsContainer>
      </div>

      {legs.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-500 dark:text-gray-400" data-testid="legs-empty">
          <div className="mb-3">
            <PermissionsContainer
              componentId="leg-add-remove-stops"
              componentName="Add/Remove Stops"
              defaultHidden={true}
              showCog={true}
            >
              <AddLegPlaceholder
                onClick={() => editing?.addLeg?.('first', 0)}
                data-testid="add-leg-empty"
              />
            </PermissionsContainer>
          </div>
          No legs.
        </div>
      ) : (
        <HoverCtx.Provider value={{ hField, setHField }}>
          {cards ? (
            <LegsCards
              legs={legs}
              stacked={stacked}
              editing={editing}
              legEditingAccess={legEditingAccess}
              arrivalDepartAccess={arrivalDepartAccess}
              canEditDeliveryLocation={canEditDeliveryLocation}
              apptClearAccess={apptClearAccess}
              apptLockOverrideAccess={apptLockOverrideAccess}
              apptEditAccess={apptEditAccess}
              deliveryLeg={deliveryLeg}
              templateGate={templateGate}
            />
          ) : (
            <div onMouseLeave={() => setHField(null)}>
              <LegsHeader />
              {legs.map((leg, i) => {
                const editsForGates = editing?.editedLegs ?? {};
                const gates = resolveLegGates(leg, allLegs, editsForGates, legEditingAccess, canEditDeliveryLocation, apptClearAccess, apptLockOverrideAccess, apptEditAccess, editing?.originalLegs, deliveryLeg, templateGate, adminOverrideActive);
                const scope = resolveScopeMembership(leg, allLegs);
                // Drawer-faithful "remove stop" button visibility, mirrors
                // OrderDetailsModalContent.tsx ~3364 / ~3621.
                const multi = legs.length > 1;
                const prev = i > 0 ? legs[i - 1] : null;
                const canRemoveOriginStop =
                  multi &&
                  !leg.isPaid &&
                  !(prev?.isPaid) &&
                  !(prev && prev.legAction2 === 'DELIVERY');
                const canRemoveDestStop =
                  i === legs.length - 1 &&
                  multi &&
                  !leg.isPaid &&
                  leg.legAction2 !== 'DELIVERY';
                return (
                  <React.Fragment key={leg.legrecno ?? leg.legnum}>
                    {i === 0 && (
                      <PermissionsContainer
                        componentId="leg-add-remove-stops"
                        componentName="Add/Remove Stops"
                        defaultHidden={true}
                        showCog={true}
                      >
                        <AddLegPlaceholder
                          onClick={() => editing?.addLeg?.('first', 0)}
                          data-testid={`add-leg-before-${leg.legnum}`}
                        />
                      </PermissionsContainer>
                    )}
                    <LegRow
                      leg={leg}
                      editing={editing}
                      gates={gates}
                      arrivalDepartDisabled={
                        !canEditArrivalDepart({ leg, arrivalDepartAccess, overrideActive: adminOverrideActive })
                        || isTemplate
                      }
                      scope={scope}
                      isFirstLeg={i === 0}
                      canRemoveOriginStop={canRemoveOriginStop}
                      canRemoveDestStop={canRemoveDestStop}
                    />
                    {i === legs.length - 1 && (
                      <PermissionsContainer
                        componentId="leg-add-remove-stops"
                        componentName="Add/Remove Stops"
                        defaultHidden={true}
                        showCog={false}
                      >
                        <AddLegPlaceholder
                          onClick={() => editing?.addLeg?.('last', legs.length)}
                          data-testid="add-leg-after-last"
                        />
                      </PermissionsContainer>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </HoverCtx.Provider>
      )}
    </PermissionsContainer>
    </div>
    <TravelHistoryModal
      isOpen={travelHistoryModal.isOpen}
      onClose={() => setTravelHistoryModal({ isOpen: false, legNum: null })}
      proNumber={pronumber !== undefined ? String(pronumber) : ''}
      legNum={travelHistoryModal.legNum ?? 0}
    />
    <DispatchInfoModal
      leg={dispatchInfoLeg}
      onClose={() => setDispatchInfoLeg(null)}
      onChange={
        editing?.updateLegField
          ? (legrecno, field, value) =>
              editing.updateLegField?.(legrecno, field, value)
          : undefined
      }
      onPayCleared={editing?.refresh ? () => { void editing.refresh?.(); } : undefined}
      onDocRejected={editing?.refresh ? () => { void editing.refresh?.(); } : undefined}
    />
    <LocationInfoModal
      location={locationInfoRecno != null ? locationInfoMap[locationInfoRecno] ?? null : null}
      onClose={() => setLocationInfoRecno(null)}
      onUpdated={(updated) => setLocationInfoMap((prev) => ({ ...prev, [updated.recno]: updated }))}
    />
    <ClearDriverConfirmModal
      isOpen={!!clearDriverConfirm}
      onCancel={() => {
        clearDriverConfirm?.revert();
        setClearDriverConfirm(null);
      }}
      onConfirm={() => {
        if (clearDriverConfirm) {
          editing?.clearDriver?.(clearDriverConfirm.legrecno, { clearDates: true });
        }
        setClearDriverConfirm(null);
      }}
    />
    <SendDispatchConfirmModal
      isOpen={!!sendDispatchLeg}
      onClose={() => setSendDispatchLeg(null)}
      dispatchRecno={sendDispatchLeg?.dispatchid ?? 0}
      pronumber={pronumber !== undefined ? String(pronumber) : ''}
      legnum={sendDispatchLeg?.legnum ?? 0}
      driverUnit={(sendDispatchLeg?.driver ?? '').trim()}
    />
    <WarningConfirmModal
      open={!!consigneeCopyPrompt}
      title="Update Consignee?"
      message={
        <>
          Update the Consignee
          {effectiveConsigneeName ? (
            <>
              {' '}
              (currently <strong>{effectiveConsigneeName}</strong>)
            </>
          ) : null}{' '}
          with <strong>{consigneeCopyPrompt?.name}</strong>?
        </>
      }
      confirmLabel="Yes, update"
      cancelLabel="No"
      onConfirm={() => {
        if (consigneeCopyPrompt) {
          editing?.updateOrderField?.('consigneeCode', consigneeCopyPrompt.recno);
          // Optimistic display name; not persisted (derived on refetch).
          editing?.updateOrderField?.('consigneeName', consigneeCopyPrompt.name);
        }
        setConsigneeCopyPrompt(null);
      }}
      onCancel={() => setConsigneeCopyPrompt(null)}
    />
    </ClearDriverCtx.Provider>
    </SendDispatchCtx.Provider>
    </DispatchInfoCtx.Provider>
    </TravelHistoryCtx.Provider>
    </LocationInfoCtx.Provider>
    </OrderContextCtx.Provider>
  );
};

interface AddLegPlaceholderProps {
  onClick?: () => void;
  'data-testid'?: string;
}

const AddLegPlaceholder: React.FC<AddLegPlaceholderProps> = ({
  onClick,
  'data-testid': testId,
}) => {
  // Street-turned Import: the control stays visible (so the user sees why) but
  // is inert and names the pro the leg belongs on.
  const { legAddBlock } = React.useContext(OrderContextCtx);
  const blocked = !!legAddBlock;
  const clickable = !!onClick && !blocked;
  return (
    <PermissionsContainer
      componentId="leg-add-remove-stops"
      componentName="Add/Remove Stops"
      showCog={false}
      defaultHidden={true}
    >
      <div
        onClick={clickable ? onClick : undefined}
        role={onClick ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-disabled={blocked ? 'true' : undefined}
        title={legAddBlock ?? undefined}
        data-testid={testId}
        className={
          'py-1 text-center text-[0.6875rem] text-slate-400 dark:text-gray-500 select-none border-b border-slate-200 dark:border-gray-700 ' +
          (clickable ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-800' : '') +
          (blocked ? 'cursor-not-allowed opacity-60' : '')
        }
      >
        {blocked ? `Street turned to pro ${reuseProFromBlock(legAddBlock)}. Add legs there.` : '+ Add Leg'}
      </div>
    </PermissionsContainer>
  );
};

/** The reuse pro named in a streetTurnLegBlock message (for the inline label). */
function reuseProFromBlock(message: string | null | undefined): string {
  return message?.match(/pro (\d+)/)?.[1] ?? '';
}

export default LegsGrid;
